import React, { useState, useEffect, useRef } from 'react';
import {
  ShoppingCart, MapPin, Clock, Phone, Plus, Minus, X, AlertCircle,
  CheckCircle, ChevronRight, ChevronLeft, Home, User,
  LogOut, Mail, Lock, Eye, EyeOff, AlertTriangle, Tag,
  UtensilsCrossed, MenuSquare, MessageSquare
} from 'lucide-react';
import { db, auth, functions } from './firebase';
import { collection, getDocs, doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import {
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  updateProfile,
  reload,
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Capacitor } from '@capacitor/core';
import { Stripe as NativeStripe } from '@capacitor-community/stripe';

// ─── Config ───────────────────────────────────────────────────────────────────
const MIN_ORDER = 15.00;
const TOPPING_PRICE = 0.75;

// Buy One Get One Free: any 2 large pizzas for a fixed total.
const BOGO_TOTAL_PRICE = 17.99;

// Image assets — place in /public/images/
const BG_IMAGE = '/images/pizzas-bg.jpg';
const LOGO_IMAGE = '/images/tomato-logo.png';

// Marinara palette
const MARINARA = '#a82e2a';
const MARINARA_DARK = '#8b2421';

// Global styles: BJcree font + global font family
// Place font file at /public/fonts/BJcree.woff2 (and/or .woff / .ttf)
const GLOBAL_STYLES = `
html, body, #root, button, input, select, textarea {
  font-family: 'Playfair Display', Georgia, serif;
}
/* Match the page background so Android WebView overscroll doesn't flash white. */
html, body {
  background-color: #a82e2a;
  overscroll-behavior: none;
  height: 100%;
  overflow: hidden;
  margin: 0;
}
/* Disable text/image selection, long-press callouts, and tap highlight so the
   app feels native. Re-enabled below for form fields where typing/selecting is
   required. */
html, body, #root {
  -webkit-user-select: none;
  user-select: none;
  -webkit-touch-callout: none;
  -webkit-tap-highlight-color: transparent;
}
img {
  -webkit-user-drag: none;
  user-drag: none;
  pointer-events: auto;
}
input, textarea, [contenteditable="true"] {
  -webkit-user-select: text;
  user-select: text;
  -webkit-touch-callout: default;
}
/* #root owns the scroll. overscroll-behavior: contain prevents iOS rubber-band
   from leaking through and exposing the marinara body background past the end
   of the content. */
#root {
  height: 100dvh;
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
}

/* ── Bouncing tomatoes hero animation ───────────────────────────── */
/* Wrapper scales the scene down to fit the app's mobile hero while
   keeping the original tuned positions/keyframes untouched. */
.tomato-scene-wrap {
  height: 196px;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  overflow: visible;
}
.tomato-scene {
  position: relative;
  width: 100%;
  max-width: 680px;
  height: 320px;
  margin: 0 auto;
  overflow: visible;
  transform: scale(0.6);
  transform-origin: bottom center;
}
.tomato {
  position: absolute;
  bottom: 0;
  height: 260px;
  width: auto;
  will-change: transform;
  filter: drop-shadow(0 10px 18px rgba(0,0,0,0.4));
}
.tomato-boy {
  left: calc(50% - 154px);
  animation: bounceBoy 3s ease-in-out 1;
}
.tomato-girl {
  left: calc(50% - 40px);
  animation: bounceGirl 3s ease-in-out 1;
}
@keyframes bounceBoy {
  0%   { transform: translate(-450px, 0); }
  14%  { transform: translate(-230px, -70px); }
  26%  { transform: translate(-150px, 0); }
  40%  { transform: translate(-80px, -55px); }
  54%  { transform: translate(-40px, 5px); }
  68%  { transform: translate(-15px, -25px); }
  80%  { transform: translate(0, 0); }
  85%  { transform: translate(3px, 0) scale(0.97, 1.03); }
  100% { transform: translate(0, 0); }
}
@keyframes bounceGirl {
  0%   { transform: translate(450px, 0); }
  14%  { transform: translate(230px, -70px); }
  26%  { transform: translate(150px, 0); }
  40%  { transform: translate(80px, -55px); }
  54%  { transform: translate(40px, 5px); }
  68%  { transform: translate(15px, -25px); }
  80%  { transform: translate(0, 0); }
  85%  { transform: translate(-3px, 0) scale(0.97, 1.03); }
  100% { transform: translate(0, 0); }
}
@media (prefers-reduced-motion: reduce) {
  .tomato-boy, .tomato-girl {
    animation: none;
    transform: translate(0, 0);
  }
}

/* ── Slow zoom-on-hover for menu thumbnails ─────────────────────── */
.zoom-wrap {
  overflow: hidden;
}
.zoom-wrap img {
  transition: transform 700ms cubic-bezier(0.22, 1, 0.36, 1);
  will-change: transform;
}
.zoom-wrap:hover img,
.zoom-wrap:focus-within img {
  transform: scale(1.18);
}
@media (prefers-reduced-motion: reduce) {
  .zoom-wrap img { transition: none; }
  .zoom-wrap:hover img, .zoom-wrap:focus-within img { transform: none; }
}
`;

// Default topping list (used when Firestore `toppings` collection is empty).
// In BOGO mode the customizer overrides all prices to BOGO_TOPPING_PRICE (£1).
const DEFAULT_TOPPINGS = [
  // Cheeses
  { id: 'top-cheese', name: 'Extra Cheese', price: TOPPING_PRICE },
  { id: 'top-buffalo-mozzarella', name: 'Buffalo Mozzarella', price: 1.00 },
  { id: 'top-parmesan', name: 'Parmesan', price: TOPPING_PRICE },
  { id: 'top-goat-cheese', name: 'Goat Cheese', price: TOPPING_PRICE },
  // Meats
  { id: 'top-pepperoni', name: 'Pepperoni', price: TOPPING_PRICE },
  { id: 'top-salami', name: 'Salami', price: TOPPING_PRICE },
  { id: 'top-prosciutto-cotto', name: 'Prosciutto Cotto', price: 1.00 },
  { id: 'top-salsiccia', name: 'Salsiccia', price: 1.00 },
  { id: 'top-chicken', name: 'Chicken', price: 1.00 },
  { id: 'top-bacon', name: 'Bacon', price: 1.00 },
  { id: 'top-beef', name: 'Beef', price: 1.00 },
  { id: 'top-anchovies', name: 'Anchovies', price: 1.00 },
  // Vegetables / herbs / fruit
  { id: 'top-green-pepper', name: 'Green Pepper', price: TOPPING_PRICE },
  { id: 'top-mushroom', name: 'Mushrooms', price: TOPPING_PRICE },
  { id: 'top-onion', name: 'Red Onion', price: TOPPING_PRICE },
  { id: 'top-olives', name: 'Olives', price: TOPPING_PRICE },
  { id: 'top-jalapeno', name: 'Jalapeños', price: TOPPING_PRICE },
  { id: 'top-fresh-chilli', name: 'Fresh Chilli', price: TOPPING_PRICE },
  { id: 'top-rocket', name: 'Rocket', price: TOPPING_PRICE },
  { id: 'top-basil', name: 'Basil', price: TOPPING_PRICE },
  { id: 'top-spinach', name: 'Spinach', price: TOPPING_PRICE },
  { id: 'top-cherry-tomato', name: 'Cherry Tomato', price: TOPPING_PRICE },
  { id: 'top-sweetcorn', name: 'Sweetcorn', price: TOPPING_PRICE },
  { id: 'top-pineapple', name: 'Pineapple', price: TOPPING_PRICE },
];

const stripePromise = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
  ? loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
  : null;

// Apple Pay merchant identifier registered in Apple Developer + Stripe Dashboard.
// Format: 'merchant.<your-domain-in-reverse>'.
const APPLE_PAY_MERCHANT_ID = 'merchant.com.pomopizza.app';

// Verification and password-reset emails are now sent by the sendVerificationEmail
// and sendPasswordResetLink Cloud Functions (branded, via SendGrid). The redirect
// URL is configured server-side in functions/index.js (PUBLIC_APP_URL).

// Until your Stripe account is live, keep Google Pay in test mode on Android.
const GOOGLE_PAY_TEST_MODE = false;

const stripeAppearance = {
  theme: 'stripe',
  variables: {
    colorPrimary: '#a82e2a',
    colorText: '#374151',
    fontSizeBase: '16px',
    borderRadius: '8px',
  },
};

// Reusable page-background wrapper. `fitToViewport` locks the page to one
// screen-height with no scroll — used by HomeScreen so users can't pull past
// the last button into empty space.
const PageBackground = ({ children, overlayOpacity = 0.5, fitToViewport = false }) => (
  <div
    className={fitToViewport ? 'h-screen overflow-hidden relative flex flex-col' : 'min-h-screen relative'}
    style={{
      backgroundImage: `url(${BG_IMAGE})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundAttachment: 'fixed',
      height: fitToViewport ? '100dvh' : undefined,
    }}
  >
    <div className="absolute inset-0 pointer-events-none" style={{ backgroundColor: `rgba(0,0,0,${overlayOpacity})` }} />
    <div className={fitToViewport ? 'relative z-10 flex-1 flex flex-col overflow-hidden' : 'relative z-10'}>{children}</div>
  </div>
);

// Shared red top bar used on Full Menu, BOGO, and Meal Deals pages.
// `paddingTop` adds the iOS notch / Android status-bar inset on top of the
// regular vertical padding so the header content isn't tucked under the OS UI
// in the native shells.
const AppHeader = ({ user, onHome, onShowProfile, onSignIn, onShowCart, cartCount }) => (
  <header
    className="text-white shadow-lg sticky top-0 z-40"
    style={{
      backgroundColor: MARINARA,
      paddingTop: 'env(safe-area-inset-top)',
    }}
  >
    <div className="max-w-7xl mx-auto px-4 py-4">
      <div className="flex justify-between items-center gap-2">
        <div className="flex items-center gap-2 flex-shrink-0">
          <button onClick={onHome} className="p-1.5 rounded-lg transition-colors hover:bg-white hover:bg-opacity-15">
            <Home size={20} />
          </button>
          <p className="text-base sm:text-lg whitespace-nowrap" style={{ color: '#f0d4d2' }}>PomoPizza</p>
        </div>
        <div className="flex gap-2 items-center flex-shrink-0">
          {user ? (
            <button onClick={onShowProfile} title="Account" className="p-2 rounded-lg flex items-center gap-1.5 text-sm hover:bg-white hover:bg-opacity-15">
              <User size={16} /> <span className="hidden sm:inline">{user.displayName || 'Account'}</span>
            </button>
          ) : (
            <button onClick={onSignIn} className="bg-white px-2.5 py-1.5 rounded-lg flex items-center gap-1.5 text-sm font-semibold" style={{ color: MARINARA }}>
              <User size={16} /> Sign In
            </button>
          )}
          <button onClick={onShowCart} aria-label={`Cart (${cartCount})`} className="bg-white px-2.5 py-1.5 rounded-lg font-semibold flex items-center gap-1.5 transition-colors shadow-md text-sm" style={{ color: MARINARA }}>
            <ShoppingCart size={18} />
            {cartCount}
          </button>
        </div>
      </div>
    </div>
  </header>
);

// ─── Auth Modal ───────────────────────────────────────────────────────────────
const AuthModal = ({ onClose, onSuccess, defaultMode = 'signin' }) => {
  const [mode, setMode] = useState(defaultMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [pendingUser, setPendingUser] = useState(null);

  const validatePhone = (p) => /^(\+44|0)[1-9]\d{9}$/.test(p.replace(/\s/g, ''));
  const validateEmail = (e) => /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/.test(e);
  const validatePassword = (pw) => {
    if (pw.length < 8) return 'Password must be at least 8 characters.';
    if (!/[A-Z]/.test(pw)) return 'Password must contain at least one uppercase letter.';
    if (!/[0-9]/.test(pw)) return 'Password must contain at least one number.';
    return null;
  };

  const friendlyError = (code) => {
    const map = {
      'auth/invalid-credential': 'Email or password is incorrect.',
      'auth/wrong-password': 'Email or password is incorrect.',
      'auth/user-not-found': 'No account found with that email.',
      'auth/email-already-in-use': 'An account already exists with this email.',
      'auth/weak-password': 'Password must be at least 8 characters with an uppercase letter and a number.',
      'auth/too-many-requests': 'Too many attempts. Please try again later.',
      'auth/network-request-failed': 'Network error. Check your connection.',
      'auth/invalid-email': 'Please enter a valid email address.',
    };
    return map[code] || 'Something went wrong. Please try again.';
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setError(''); setInfo('');
    if (!name || name.length < 2) { setError('Please enter your name.'); return; }
    if (!validateEmail(email)) { setError('Please enter a valid email.'); return; }
    if (!validatePhone(phone)) { setError('Please enter a valid UK phone number.'); return; }
    const pwError = validatePassword(password);
    if (pwError) { setError(pwError); return; }

    setLoading(true);
    try {
      // 1. Create the auth user
      const cred = await createUserWithEmailAndPassword(auth, email, password);

      // 2. Update display name (non-critical) — do this before the verification
      //    email so the email can greet the user by name.
      try {
        await updateProfile(cred.user, { displayName: name });
      } catch (profileErr) {
        console.error('Failed to update profile:', profileErr);
      }

      // 3. Write user doc to Firestore (non-critical — can be retried later)
      try {
        await setDoc(doc(db, 'users', cred.user.uid), {
          name, email, phone, marketingOptIn,
          createdAt: serverTimestamp(),
          emailVerified: false,
        });
      } catch (dbErr) {
        console.error('Failed to save user profile to Firestore:', dbErr);
        // Not fatal — user can still verify and use the app
      }

      // 4. Send the branded verification email via our Cloud Function (SendGrid).
      try {
        await httpsCallable(functions, 'sendVerificationEmail')();
      } catch (verifyErr) {
        console.error('Failed to send verification email:', verifyErr);
      }

      setPendingUser(cred.user);
      setMode('verify');
      setInfo(`We sent a verification link to ${email}. Please check your inbox (and spam folder).`);
    } catch (err) {
      // This catch only fires if the auth creation itself failed
      setError(friendlyError(err.code));
    } finally {
      setLoading(false);
    }
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    setError(''); setInfo('');
    if (!validateEmail(email)) { setError('Please enter a valid email.'); return; }
    if (!password) { setError('Please enter your password.'); return; }
    setLoading(true);
    try {
      const cred = await signInWithEmailAndPassword(auth, email, password);
      if (!cred.user.emailVerified) {
        setPendingUser(cred.user);
        setMode('verify');
        setInfo('Please verify your email to continue.');
        return;
      }
      onSuccess && onSuccess(cred.user);
      onClose();
    } catch (err) { setError(friendlyError(err.code)); }
    finally { setLoading(false); }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setError(''); setInfo('');
    if (!validateEmail(email)) { setError('Please enter a valid email.'); return; }
    setLoading(true);
    try {
      await httpsCallable(functions, 'sendPasswordResetLink')({ email });
      setInfo(`If an account exists for ${email}, a password reset link is on its way.`);
    } catch (err) { setError(friendlyError(err.code)); }
    finally { setLoading(false); }
  };

  const handleResendVerification = async () => {
    if (!pendingUser) return;
    setLoading(true); setError(''); setInfo('');
    try {
      await httpsCallable(functions, 'sendVerificationEmail')();
      setInfo('Verification email resent. Check your inbox.');
    } catch (err) { setError(friendlyError(err.code)); }
    finally { setLoading(false); }
  };

  const handleCheckVerification = async () => {
    if (!pendingUser) return;
    setLoading(true); setError(''); setInfo('');
    try {
      await reload(pendingUser);
      if (pendingUser.emailVerified) {
        // Force a fresh ID token so the email_verified claim the Cloud
        // Functions read is up to date (reload() alone doesn't refresh it).
        await pendingUser.getIdToken(true);
        await setDoc(doc(db, 'users', pendingUser.uid), { emailVerified: true }, { merge: true });
        try {
          await httpsCallable(functions, 'sendWelcomeEmail')();
        } catch (mailErr) {
          console.warn('Welcome email failed (non-fatal):', mailErr);
        }
        onSuccess && onSuccess(pendingUser);
        onClose();
      } else {
        setError('Email not verified yet. Please click the link in your inbox.');
      }
    } catch (err) { setError(friendlyError(err.code)); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="text-white p-6 rounded-t-2xl flex justify-between items-center" style={{ backgroundColor: MARINARA }}>
          <h3 className="text-2xl font-bold">
            {mode === 'signin' && 'Welcome Back'}
            {mode === 'signup' && 'Create Account'}
            {mode === 'forgot' && 'Reset Password'}
            {mode === 'verify' && 'Verify Email'}
          </h3>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-white hover:bg-opacity-15"><X size={20} /></button>
        </div>

        <div className="p-6">
          {error && (
            <div className="mb-4 flex items-center gap-2 text-red-600 text-sm bg-red-50 p-3 rounded-lg">
              <AlertCircle size={16} /> {error}
            </div>
          )}
          {info && (
            <div className="mb-4 flex items-center gap-2 text-green-700 text-sm bg-green-50 p-3 rounded-lg">
              <CheckCircle size={16} /> {info}
            </div>
          )}

          {mode === 'signin' && (
            <form onSubmit={handleSignIn} className="space-y-4">
              <div className="relative">
                <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2"
                  style={{ '--tw-ring-color': MARINARA }} />
              </div>
              <div className="relative">
                <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type={showPassword ? 'text' : 'password'} placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-10 py-3 border rounded-lg focus:outline-none focus:ring-2"
                  style={{ '--tw-ring-color': MARINARA }} />
                <button type="button" onClick={() => setShowPassword((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <button type="button" onClick={() => { setMode('forgot'); setError(''); setInfo(''); }} className="text-sm hover:underline" style={{ color: MARINARA }}>
                Forgot password?
              </button>
              <button type="submit" disabled={loading}
                className="w-full text-white py-3 rounded-lg font-bold disabled:bg-gray-400 transition-colors"
                style={{ backgroundColor: loading ? undefined : MARINARA }}
                onMouseEnter={(e) => !loading && (e.currentTarget.style.backgroundColor = MARINARA_DARK)}
                onMouseLeave={(e) => !loading && (e.currentTarget.style.backgroundColor = MARINARA)}>
                {loading ? 'Signing in...' : 'Sign In'}
              </button>
              <p className="text-sm text-center text-gray-600">
                Don't have an account?{' '}
                <button type="button" onClick={() => { setMode('signup'); setError(''); setInfo(''); }} className="font-semibold hover:underline" style={{ color: MARINARA }}>
                  Sign up
                </button>
              </p>
            </form>
          )}

          {mode === 'signup' && (
            <form onSubmit={handleSignUp} className="space-y-4">
              <input type="text" placeholder="Full Name" value={name} onChange={(e) => setName(e.target.value)}
                className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2" style={{ '--tw-ring-color': MARINARA }} />
              <div className="relative">
                <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2" style={{ '--tw-ring-color': MARINARA }} />
              </div>
              <input type="tel" placeholder="UK Phone Number (e.g. 07123456789)" value={phone} onChange={(e) => setPhone(e.target.value)}
                className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2" style={{ '--tw-ring-color': MARINARA }} />
              <div className="relative">
                <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type={showPassword ? 'text' : 'password'} placeholder="Password (8+ chars, 1 uppercase, 1 number)" value={password} onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-10 py-3 border rounded-lg focus:outline-none focus:ring-2" style={{ '--tw-ring-color': MARINARA }} />
                <button type="button" onClick={() => setShowPassword((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <label className="flex items-start gap-3 cursor-pointer p-3 bg-amber-50 rounded-lg border border-amber-200">
                <input type="checkbox" checked={marketingOptIn} onChange={(e) => setMarketingOptIn(e.target.checked)}
                  className="mt-1 w-5 h-5" />
                <div className="flex items-start gap-2">
                  <MessageSquare size={18} className="flex-shrink-0 mt-0.5" style={{ color: MARINARA }} />
                  <div>
                    <p className="text-sm font-semibold text-gray-800">Get exclusive offers by SMS</p>
                    <p className="text-xs text-gray-600 mt-1">Be the first to hear about deals, discounts and new pizzas. Opt out anytime.</p>
                  </div>
                </div>
              </label>
              <button type="submit" disabled={loading}
                className="w-full text-white py-3 rounded-lg font-bold disabled:bg-gray-400 transition-colors"
                style={{ backgroundColor: loading ? undefined : MARINARA }}
                onMouseEnter={(e) => !loading && (e.currentTarget.style.backgroundColor = MARINARA_DARK)}
                onMouseLeave={(e) => !loading && (e.currentTarget.style.backgroundColor = MARINARA)}>
                {loading ? 'Creating account...' : 'Create Account'}
              </button>
              <p className="text-sm text-center text-gray-600">
                Already have an account?{' '}
                <button type="button" onClick={() => { setMode('signin'); setError(''); setInfo(''); }} className="font-semibold hover:underline" style={{ color: MARINARA }}>
                  Sign in
                </button>
              </p>
            </form>
          )}

          {mode === 'forgot' && (
            <form onSubmit={handleForgot} className="space-y-4">
              <p className="text-sm text-gray-600">Enter your email and we'll send you a link to reset your password.</p>
              <div className="relative">
                <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2" style={{ '--tw-ring-color': MARINARA }} />
              </div>
              <button type="submit" disabled={loading}
                className="w-full text-white py-3 rounded-lg font-bold disabled:bg-gray-400"
                style={{ backgroundColor: MARINARA }}>
                {loading ? 'Sending...' : 'Send Reset Link'}
              </button>
              <button type="button" onClick={() => { setMode('signin'); setError(''); setInfo(''); }} className="w-full text-gray-600 py-2 text-sm hover:text-gray-800">
                ← Back to sign in
              </button>
            </form>
          )}

          {mode === 'verify' && (
            <div className="space-y-4 text-center">
              <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto">
                <Mail size={36} className="text-amber-700" />
              </div>
              <p className="text-gray-700">
                We've sent a verification link to <span className="font-semibold">{pendingUser?.email || email}</span>
              </p>
              <p className="text-sm text-gray-500">Click the link in the email, then click "I've verified" below.</p>
              <button onClick={handleCheckVerification} disabled={loading}
                className="w-full text-white py-3 rounded-lg font-bold disabled:bg-gray-400"
                style={{ backgroundColor: MARINARA }}>
                {loading ? 'Checking...' : "I've verified my email"}
              </button>
              <button onClick={handleResendVerification} disabled={loading} className="w-full py-2 text-sm hover:underline" style={{ color: MARINARA }}>
                Resend verification email
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Payment Form ─────────────────────────────────────────────────────────────
const PaymentForm = ({ orderId, total, onSuccess, onError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setProcessing(true);
    setPaymentError('');

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/?payment_status=return&order_id=${encodeURIComponent(orderId)}`,
      },
      redirect: 'if_required',
    });

    if (error) {
      setPaymentError(error.message || 'Payment failed.');
      onError(error.message || 'Payment failed.');
      setProcessing(false);
      return;
    }
    if (paymentIntent && paymentIntent.status === 'succeeded') {
      onSuccess(orderId);
    } else if (paymentIntent && paymentIntent.status === 'processing') {
      onSuccess(orderId);
    } else {
      setPaymentError('Payment did not complete. Please try again.');
      onError('Payment did not complete.');
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement options={{ layout: 'tabs' }} />
      {paymentError && (
        <div className="flex items-center gap-2 text-red-500 text-sm bg-red-50 p-3 rounded-lg">
          <AlertCircle size={16} />{paymentError}
        </div>
      )}
      <button type="submit" disabled={!stripe || !elements || processing}
        className="w-full text-white py-4 rounded-lg font-bold disabled:bg-gray-400 flex items-center justify-center gap-2"
        style={{ backgroundColor: (!stripe || !elements || processing) ? undefined : MARINARA }}>
        {processing ? <><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>Processing…</> : <>Pay £{total.toFixed(2)}</>}
      </button>
      <p className="text-xs text-gray-500 text-center">Your payment is secured by Stripe</p>
    </form>
  );
};

// ─── Profile Modal ───────────────────────────────────────────────────────────
const ProfileModal = ({ user, profile, onClose, onSave, onSignOut, onDeleteAccount, onShowPrivacy }) => {
  const [form, setForm] = useState({
    name: profile?.name || user?.displayName || '',
    phone: profile?.phone || '',
    address: profile?.address || '',
    postcode: profile?.postcode || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [resettingPw, setResettingPw] = useState(false);
  const [pwResetSent, setPwResetSent] = useState(false);

  const handleSave = async () => {
    setSaving(true); setError(''); setSaved(false);
    try {
      await onSave(form);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setError(err?.message || 'Could not save details.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true); setError('');
    try {
      await onDeleteAccount();
    } catch (err) {
      setError(err?.message || 'Could not delete account.');
      setDeleting(false);
    }
  };

  const handleResetPassword = async () => {
    if (!user?.email) return;
    setResettingPw(true); setError(''); setPwResetSent(false);
    try {
      await httpsCallable(functions, 'sendPasswordResetLink')({ email: user.email });
      setPwResetSent(true);
      setTimeout(() => setPwResetSent(false), 8000);
    } catch (err) {
      setError(err?.message || 'Could not send password reset email.');
    } finally {
      setResettingPw(false);
    }
  };

  const field = (key, label, type = 'text', placeholder = '') => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <input
        type={type}
        value={form[key]}
        placeholder={placeholder}
        onChange={(e) => setForm((p) => ({ ...p, [key]: e.target.value }))}
        className="w-full px-4 py-3 border rounded-lg"
      />
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-lg max-w-md w-full my-8">
        <div className="p-6 border-b flex justify-between items-center">
          <h3 className="text-2xl font-bold">My Account</h3>
          <button onClick={onClose}><X size={24} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input type="email" value={user?.email || ''} disabled className="w-full px-4 py-3 border rounded-lg bg-gray-50 text-gray-500" />
          </div>
          {field('name', 'Name')}
          {field('phone', 'Phone', 'tel', '+44…')}
          {field('address', 'Address')}
          {field('postcode', 'Postcode')}
          {error && <div className="text-red-500 text-sm">{error}</div>}
          {saved && <div className="text-green-700 text-sm">Saved.</div>}
          <button onClick={handleSave} disabled={saving}
            className="w-full text-white py-3 rounded-lg font-bold disabled:bg-gray-400"
            style={{ backgroundColor: saving ? undefined : MARINARA }}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
          <button onClick={handleResetPassword} disabled={resettingPw}
            className="w-full text-gray-700 border border-gray-300 py-3 rounded-lg font-semibold hover:bg-gray-50 flex items-center justify-center gap-2 disabled:opacity-50">
            <Lock size={16} /> {resettingPw ? 'Sending…' : 'Reset Password'}
          </button>
          {pwResetSent && (
            <div className="text-green-700 text-sm">
              Password reset link sent to {user?.email}. Check your inbox.
            </div>
          )}
          <button onClick={onSignOut}
            className="w-full text-gray-700 border border-gray-300 py-3 rounded-lg font-semibold hover:bg-gray-50 flex items-center justify-center gap-2">
            <LogOut size={16} /> Sign Out
          </button>

          <div className="border-t pt-4 space-y-3">
            <button onClick={onShowPrivacy} className="w-full text-sm text-gray-600 hover:text-gray-900 underline">
              Privacy Policy
            </button>

            {!confirmDelete ? (
              <button onClick={() => setConfirmDelete(true)}
                className="w-full text-red-600 border border-red-300 py-3 rounded-lg font-semibold hover:bg-red-50 flex items-center justify-center gap-2">
                Delete Account
              </button>
            ) : (
              <div className="space-y-3 bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-800 font-semibold">Permanently delete your account?</p>
                <p className="text-xs text-red-700">
                  Your profile and saved details will be removed. Past order receipts are kept for accounting but anonymised. This cannot be undone.
                </p>
                <div className="flex gap-2">
                  <button onClick={() => setConfirmDelete(false)} disabled={deleting}
                    className="flex-1 text-gray-700 border border-gray-300 py-2 rounded-lg text-sm font-semibold hover:bg-gray-50">
                    Cancel
                  </button>
                  <button onClick={handleDelete} disabled={deleting}
                    className="flex-1 bg-red-600 text-white py-2 rounded-lg text-sm font-bold disabled:bg-gray-400">
                    {deleting ? 'Deleting…' : 'Yes, delete'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Privacy Policy Modal ────────────────────────────────────────────────────
const PrivacyPolicyModal = ({ onClose }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto">
    <div className="bg-white rounded-lg max-w-2xl w-full my-8 max-h-[90vh] overflow-y-auto">
      <div className="p-6 border-b flex justify-between items-center sticky top-0 bg-white">
        <h3 className="text-2xl font-bold">Privacy Policy</h3>
        <button onClick={onClose}><X size={24} /></button>
      </div>
      <div className="p-6 space-y-4 text-sm text-gray-700 leading-relaxed">
        <p className="text-xs text-gray-500">Last updated: 27 May 2026 · Full version: <a href="https://pomopizza.uk/privacy/" className="underline" style={{ color: MARINARA }}>pomopizza.uk/privacy</a></p>

        <h4 className="font-bold text-gray-900">1. Who we are</h4>
        <p>
          PomoPizza is operated by <strong>Pomo Farm Limited</strong>, a company
          registered in England and Wales (company number 16676469), with its
          registered office at 3 Charleville Road, London W14 9JL. We are the
          data controller for personal data you provide.
        </p>

        <h4 className="font-bold text-gray-900">2. What we collect</h4>
        <ul className="list-disc pl-5 space-y-1">
          <li>Account details: email address, name, phone number.</li>
          <li>Delivery details: street address and postcode.</li>
          <li>Order history: items ordered, totals, timestamps.</li>
          <li>Payment data is handled directly by Stripe; we never see or store your full card number.</li>
        </ul>

        <h4 className="font-bold text-gray-900">3. Why we use it</h4>
        <ul className="list-disc pl-5 space-y-1">
          <li>To create your account and let you sign in.</li>
          <li>To process and deliver your orders.</li>
          <li>To send order confirmations and account verification emails.</li>
          <li>To check that your delivery address is within our delivery radius (we send the address to Google Maps for distance calculation).</li>
        </ul>

        <h4 className="font-bold text-gray-900">4. Who we share it with</h4>
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Stripe</strong> — for payment processing.</li>
          <li><strong>Google Maps</strong> — to look up your address and calculate driving distance.</li>
          <li><strong>SendGrid</strong> — to send transactional emails.</li>
          <li><strong>Google Firebase</strong> — to store your account and order data.</li>
        </ul>
        <p>We do not sell your data and we do not share it for advertising.</p>

        <h4 className="font-bold text-gray-900">5. How long we keep it</h4>
        <p>
          Account data is kept while your account exists. Order receipts may be kept
          for up to 7 years to meet UK tax record-keeping requirements; if you delete
          your account these receipts are anonymised so they can no longer be tied to you.
        </p>

        <h4 className="font-bold text-gray-900">6. Your rights</h4>
        <p>
          Under UK GDPR you have the right to access, correct, delete, restrict,
          object to, and port your personal data, and to withdraw consent where we
          rely on it. You can view and edit your profile from the "My Account"
          screen and permanently delete your account from the same screen. If you
          no longer have the app installed, you can request deletion at
          pomopizza.uk/delete-account. For any other request, email
          support@pomopizza.uk and we will respond within one month. You may
          also complain to the UK Information Commissioner's Office (ico.org.uk).
        </p>

        <h4 className="font-bold text-gray-900">7. Children</h4>
        <p>
          PomoPizza is intended for users aged 18 and over. We do not knowingly
          collect personal data from anyone under 18.
        </p>

        <h4 className="font-bold text-gray-900">8. Changes to this policy</h4>
        <p>
          If we change this policy we'll update the date at the top. Material changes
          will be notified by email or in-app.
        </p>

        <button onClick={onClose} className="w-full text-white py-3 rounded-lg font-bold mt-4"
          style={{ backgroundColor: MARINARA }}>
          Close
        </button>
      </div>
    </div>
  </div>
);

// ─── Pizza Customizer ────────────────────────────────────────────────────────
// Props:
//   bogoMode    – when true: every topping is flat £1, no dip section,
//                 no "free choice" allowance. Confirm calls `onConfirm`
//                 instead of `onAddToCart` so the BOGO flow can stitch the
//                 selection into its own bundle math.
//   onConfirm   – optional. ({pizza, toppings, toppingsTotal}) => void.
//                 If set, replaces the cart-add behavior of the confirm button.
const BOGO_TOPPING_PRICE = 1.00;
const PizzaCustomizer = ({ pizza, menuItems, onClose, onAddToCart, bogoMode = false, onConfirm }) => {
  const [toppingQty, setToppingQty] = useState({});
  const [dipQty, setDipQty] = useState({});

  // In BOGO mode, override each topping's price to a flat £1.
  const rawToppings = (menuItems.toppings && menuItems.toppings.length > 0) ? menuItems.toppings : DEFAULT_TOPPINGS;
  const toppings = bogoMode ? rawToppings.map((t) => ({ ...t, price: BOGO_TOPPING_PRICE })) : rawToppings;
  const dips = bogoMode ? [] : (menuItems.dips || []);

  const isFreeChoice = !bogoMode && (pizza?.name || '').toLowerCase().includes('free choice');
  const FREE_ALLOWANCE = isFreeChoice ? 4 : 0;

  const incTopping = (id) => setToppingQty((prev) => ({ ...prev, [id]: (prev[id] || 0) + 1 }));
  const decTopping = (id) => setToppingQty((prev) => {
    const next = { ...prev, [id]: Math.max(0, (prev[id] || 0) - 1) };
    if (next[id] === 0) delete next[id];
    return next;
  });
  const incDip = (id) => setDipQty((prev) => ({ ...prev, [id]: (prev[id] || 0) + 1 }));
  const decDip = (id) => setDipQty((prev) => {
    const next = { ...prev, [id]: Math.max(0, (prev[id] || 0) - 1) };
    if (next[id] === 0) delete next[id];
    return next;
  });

  // Flatten topping units into per-unit prices so we can apply the "first N free" rule
  // to the most expensive units first (best value for the customer).
  const toppingUnitPrices = [];
  toppings.forEach((t) => {
    const q = toppingQty[t.id] || 0;
    for (let i = 0; i < q; i++) toppingUnitPrices.push(t.price);
  });
  toppingUnitPrices.sort((a, b) => b - a);

  const totalToppingUnits = toppingUnitPrices.length;
  const freeUnitsUsed = Math.min(FREE_ALLOWANCE, totalToppingUnits);
  const freeUnitsRemaining = Math.max(0, FREE_ALLOWANCE - freeUnitsUsed);
  const toppingsTotal = toppingUnitPrices.slice(freeUnitsUsed).reduce((s, p) => s + p, 0);

  const dipsTotal = dips.reduce((s, d) => s + (dipQty[d.id] || 0) * d.price, 0);
  // In BOGO mode we don't show the pizza's base price (it's absorbed into the bundle);
  // the customizer's "Total" is the topping uplift only.
  const grandTotal = bogoMode
    ? toppingsTotal
    : pizza.price + toppingsTotal + dipsTotal;

  const handleConfirm = () => {
    const selectedToppingsList = toppings
      .filter((t) => (toppingQty[t.id] || 0) > 0)
      .map((t) => ({ ...t, qty: toppingQty[t.id] }));

    // BOGO mode hands the selection back to the BOGO flow instead of adding to cart.
    if (onConfirm) {
      onConfirm({
        pizza,
        toppings: selectedToppingsList,
        toppingsTotal,
      });
      onClose();
      return;
    }

    const pizzaToAdd = selectedToppingsList.length > 0
      ? {
          ...pizza,
          id: `${pizza.id}-${Date.now()}`,
          name: `${pizza.name} (+ ${selectedToppingsList.map((t) => t.qty > 1 ? `${t.name} x${t.qty}` : t.name).join(', ')})`,
          price: pizza.price + toppingsTotal,
        }
      : pizza;
    onAddToCart(pizzaToAdd);
    dips.forEach((d) => {
      const q = dipQty[d.id] || 0;
      for (let i = 0; i < q; i++) onAddToCart(d);
    });
    onClose();
  };

  const StepperButton = ({ onClick, disabled, children, ariaLabel }) => (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      disabled={disabled}
      aria-label={ariaLabel}
      className="w-7 h-7 rounded-full flex items-center justify-center font-bold text-base transition-all disabled:opacity-40 disabled:cursor-not-allowed"
      style={{
        backgroundColor: disabled ? '#e5e7eb' : MARINARA,
        color: disabled ? '#9ca3af' : '#fff',
      }}>
      {children}
    </button>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="text-white p-6 rounded-t-2xl flex justify-between items-start" style={{ backgroundColor: MARINARA }}>
          <div>
            <p className="text-sm mb-1" style={{ color: '#f0d4d2' }}>Customize your pizza</p>
            <h3 className="text-xl font-bold">{pizza.name}</h3>
            {bogoMode
              ? <p className="text-xs mt-1" style={{ color: '#f5e0dd' }}>Toppings: £{BOGO_TOPPING_PRICE.toFixed(2)} each</p>
              : <p style={{ color: '#f5e0dd' }}>£{pizza.price.toFixed(2)}</p>}
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-white hover:bg-opacity-15"><X size={20} /></button>
        </div>

        <div className="p-6">
          <h4 className="font-bold text-gray-800 mb-1">Extra Toppings</h4>
          {isFreeChoice ? (
            <p className="text-gray-500 text-sm mb-4">
              First 4 toppings free.{' '}
              {freeUnitsRemaining > 0
                ? <span style={{ color: MARINARA, fontWeight: 600 }}>{freeUnitsRemaining} free left</span>
                : <span style={{ color: MARINARA, fontWeight: 600 }}>Free toppings used — extras charged</span>}
            </p>
          ) : (
            <p className="text-gray-500 text-sm mb-4">Add as many as you like</p>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-6">
            {toppings.map((t) => {
              const qty = toppingQty[t.id] || 0;
              const selected = qty > 0;
              return (
                <div key={t.id}
                  className="p-3 rounded-xl border-2 transition-all text-sm"
                  style={{
                    borderColor: selected ? MARINARA : '#f3f4f6',
                    backgroundColor: selected ? '#f7e8e7' : '#f9fafb',
                  }}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-gray-800 truncate">{t.name}</span>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <StepperButton onClick={() => decTopping(t.id)} disabled={qty === 0} ariaLabel={`Remove one ${t.name}`}>−</StepperButton>
                      <span className="w-5 text-center font-bold text-gray-800">{qty}</span>
                      <StepperButton onClick={() => incTopping(t.id)} ariaLabel={`Add one ${t.name}`}>+</StepperButton>
                    </div>
                  </div>
                  <p className="text-xs font-bold mt-1" style={{ color: MARINARA }}>+£{t.price.toFixed(2)} each</p>
                </div>
              );
            })}
          </div>

          {dips.length > 0 && (
            <>
              <h4 className="font-bold text-gray-800 mb-1">Add a Dip</h4>
              <p className="text-gray-500 text-sm mb-4">Perfect with sourdough crust</p>
              <div className="space-y-2 mb-6">
                {dips.map((d) => {
                  const qty = dipQty[d.id] || 0;
                  const selected = qty > 0;
                  return (
                    <div key={d.id}
                      className="flex items-center justify-between p-3 rounded-xl border-2 transition-all"
                      style={{
                        borderColor: selected ? MARINARA : '#f3f4f6',
                        backgroundColor: selected ? '#f7e8e7' : '#f9fafb',
                      }}>
                      <div className="flex items-center gap-3 min-w-0">
                        {d.image && <img src={d.image} alt={d.name} loading="lazy" decoding="async" className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />}
                        <div className="min-w-0">
                          <p className="font-medium text-gray-800 text-sm truncate">{d.name}</p>
                          {d.description && <p className="text-xs text-gray-500 truncate">{d.description}</p>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="font-bold text-sm" style={{ color: MARINARA }}>+£{d.price.toFixed(2)}</span>
                        <StepperButton onClick={() => decDip(d.id)} disabled={qty === 0} ariaLabel={`Remove one ${d.name}`}>−</StepperButton>
                        <span className="w-5 text-center font-bold text-gray-800">{qty}</span>
                        <StepperButton onClick={() => incDip(d.id)} ariaLabel={`Add one ${d.name}`}>+</StepperButton>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          <div className="border-t pt-4">
            {isFreeChoice && freeUnitsUsed > 0 && (
              <div className="flex justify-between text-sm mb-1 text-gray-500">
                <span>{freeUnitsUsed} free topping{freeUnitsUsed === 1 ? '' : 's'} applied</span>
                <span>−£{toppingUnitPrices.slice(0, freeUnitsUsed).reduce((s, p) => s + p, 0).toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between font-bold text-lg mb-3">
              <span>{bogoMode ? 'Toppings add' : 'Total'}</span>
              <span style={{ color: MARINARA }}>
                {bogoMode && grandTotal > 0 ? '+' : ''}£{grandTotal.toFixed(2)}
              </span>
            </div>
            <button onClick={handleConfirm}
              className="w-full text-white py-4 rounded-xl font-bold transition-colors"
              style={{ backgroundColor: MARINARA }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = MARINARA_DARK}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = MARINARA}>
              {bogoMode
                ? (grandTotal > 0 ? `Confirm — +£${grandTotal.toFixed(2)} toppings` : 'Confirm — no toppings')
                : `Add to Cart — £${grandTotal.toFixed(2)}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Wizard Progress ─────────────────────────────────────────────────────────
const WizardProgress = ({ current, total }) => (
  <div className="flex justify-center gap-2 mt-3">
    {Array.from({ length: total }).map((_, i) => (
      <div key={i}
        className={`h-1.5 rounded-full transition-all ${i === current ? 'bg-white w-8' : i < current ? 'bg-white bg-opacity-70 w-4' : 'bg-white bg-opacity-30 w-4'}`} />
    ))}
  </div>
);

// Shown on later steps of the BOGO / Meal Deal wizards so the customer can see
// what they've already chosen. Each item: { label, name, toppings?: [{name,qty}] }.
const SelectionsSummary = ({ items }) => {
  if (!items || items.length === 0) return null;
  return (
    <div className="mb-4 bg-white bg-opacity-95 backdrop-blur-md rounded-xl shadow-lg overflow-hidden">
      <div className="px-4 py-2.5 flex items-center gap-2 border-b border-gray-100"
        style={{ backgroundColor: '#fdf3f2' }}>
        <CheckCircle size={14} style={{ color: MARINARA }} />
        <p className="text-xs font-bold uppercase tracking-wide" style={{ color: MARINARA }}>
          Your picks so far
        </p>
      </div>
      <div className="divide-y divide-gray-100">
        {items.map((it, idx) => (
          <div key={idx} className="px-4 py-2.5 flex items-start gap-3">
            <div className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold mt-0.5"
              style={{ backgroundColor: '#fdf3f2', color: MARINARA }}>
              {idx + 1}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold leading-tight">
                {it.label}
              </p>
              <p className="text-sm font-semibold text-gray-800 truncate leading-tight mt-0.5">
                {it.name}
              </p>
              {it.toppings && it.toppings.length > 0 && (
                <p className="text-xs text-gray-500 truncate mt-0.5">
                  + {it.toppings.map((t) => t.qty > 1 ? `${t.name} x${t.qty}` : t.name).join(', ')}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── BOGO Page (full screen) ──────────────────────────────────────────────────
// Flow: pick pizza 1 → customize → pick pizza 2 → customize → review/confirm.
// Bundle math: base £17.99 covers both pizzas; each topping adds a flat £1.
// We add each pizza to the cart at (menu price + £1 × num toppings), then add
// a discount line that cancels the menu prices down to £17.99 — leaving the
// topping uplift on top.
// Reusable pizza grid. Pass either:
//   onPick                       — single full-width button (label via buttonLabel)
//   onAdd + onCustomize          — two side-by-side buttons (Add / Customize)
const PizzaGrid = ({ pizzas, onPick, onAdd, onCustomize, buttonLabel = 'Customize' }) => {
  const dual = !!(onAdd && onCustomize);
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {pizzas.length === 0 ? (
        <p className="col-span-full text-center text-gray-400 py-8">No pizzas available yet.</p>
      ) : (
        pizzas.map((p) => (
          <div key={`${p.id}-${p.sizeLabel || ''}`} className="bg-white rounded-lg shadow-md overflow-hidden flex flex-col">
            {p.image && (
              <div className="zoom-wrap w-full h-48">
                <img src={p.image} alt={p.name} loading="lazy" decoding="async" className="w-full h-full object-cover" />
              </div>
            )}
            <div className="p-5 flex flex-col flex-1">
              <h4 className="text-xl font-bold mb-1">{p.name}</h4>
              {p.description && <p className="text-gray-600 text-sm mb-4 italic flex-1">{p.description}</p>}
              {dual ? (
                <div className="flex gap-2 mt-auto">
                  <button
                    onClick={() => onAdd(p)}
                    className="flex-1 text-white py-2.5 rounded-lg font-semibold transition-colors"
                    style={{ backgroundColor: MARINARA }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = MARINARA_DARK}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = MARINARA}>
                    Add
                  </button>
                  <button
                    onClick={() => onCustomize(p)}
                    className="flex-1 py-2.5 rounded-lg font-semibold transition-colors border-2"
                    style={{ borderColor: MARINARA, color: MARINARA, backgroundColor: 'white' }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f7e8e7'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'white'; }}>
                    Customize
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => onPick(p)}
                  className="w-full text-white py-2.5 rounded-lg font-semibold transition-colors mt-auto"
                  style={{ backgroundColor: MARINARA }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = MARINARA_DARK}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = MARINARA}>
                  {buttonLabel}
                </button>
              )}
            </div>
          </div>
        ))
      )}
    </div>
  );
};

// Steps:
//   0 = pick pizza 1
//   1 = pick pizza 2
//   2 = drinks (optional)
//   3 = extras: starters / desserts / dips (optional)
//   4 = review / confirm
const BOGO_STEPS = 5;
const BOGO_REVIEW_STEP = 4;

const BOGOPage = ({ menuItems, onBack, onAddToCart, user, onSignIn, onShowProfile, onShowCart, cartCount, editingBundle, onEditDone }) => {
  // Reconstruct full state (pizzas + sides) from a previous bundle when editing.
  const restoreFromBundle = () => {
    if (!editingBundle || editingBundle.bundleType !== 'bogo') return { s1: null, s2: null, sides: {} };
    const slots = [null, null];
    const sidesMap = {};
    editingBundle.items
      .filter((c) => !c.isDiscount)
      .forEach((c) => {
        const baseId = String(c.id).replace(/-\d{13,}$/, '');
        if (c._bundleSlot === 'side') {
          sidesMap[baseId] = (sidesMap[baseId] || 0) + (c.quantity || 1);
          return;
        }
        const idx = (c._bundleSlot || 1) - 1;
        const toppings = c._bundleToppings || [];
        const toppingsTotal = toppings.reduce((s, t) => s + t.qty * BOGO_TOPPING_PRICE, 0);
        // Pull the pizza fields off the cart item; strip the "(+ toppings)" suffix from name.
        const pizza = { ...c, id: baseId, name: c.name.replace(/ \(\+ .+\)$/, ''), price: c.price - toppingsTotal };
        slots[idx] = { pizza, toppings, toppingsTotal };
      });
    return { s1: slots[0], s2: slots[1], sides: sidesMap };
  };
  const { s1: initialSlot1, s2: initialSlot2, sides: initialSides } = restoreFromBundle();

  // Editing reopens at the start of the flow — selections are still prefilled so
  // the customer can blow past steps they don't want to change via "Keep" buttons.
  const [step, setStep] = useState(0);
  // Each slot holds: { pizza: <menuItem>, toppings: [{ id, name, price, qty }], toppingsTotal }
  const [slot1, setSlot1] = useState(initialSlot1);
  const [slot2, setSlot2] = useState(initialSlot2);
  // Side items selected during the drinks/extras steps. Shape: { [menuItemId]: qty }.
  const [sides, setSides] = useState(initialSides);
  // Pending pizza waiting for customization (after the user clicked a card).
  const [customizing, setCustomizing] = useState(null); // { pizza, forSlot: 1 | 2 }

  const allPizzas = [
    ...(menuItems.pizza12 || []).map((p) => ({ ...p, sizeLabel: '12"' })),
    ...(menuItems.pizza14 || []).map((p) => ({ ...p, sizeLabel: '14"' })),
  ];

  // Side item lookups (drinks, starters, desserts, dips).
  const drinks = menuItems.softDrinks || [];
  const starters = menuItems.starters || [];
  const desserts = menuItems.desserts || [];
  const dipItems = menuItems.dips || [];
  const allSideItems = [...drinks, ...starters, ...desserts, ...dipItems];
  const findSideItem = (id) => allSideItems.find((i) => i.id === id);
  const sidesTotal = Object.entries(sides).reduce((s, [id, qty]) => {
    const item = findSideItem(id);
    return s + (item ? item.price * qty : 0);
  }, 0);
  const totalSideUnits = Object.values(sides).reduce((s, q) => s + q, 0);

  const incSide = (id) => setSides((p) => ({ ...p, [id]: (p[id] || 0) + 1 }));
  const decSide = (id) => setSides((p) => {
    const next = { ...p, [id]: Math.max(0, (p[id] || 0) - 1) };
    if (next[id] === 0) delete next[id];
    return next;
  });

  const startCustomize = (pizza) => {
    setCustomizing({ pizza, forSlot: step === 0 ? 1 : 2 });
  };

  // Quick-add: skip the topping modal, save the pizza with no toppings.
  const quickAdd = (pizza) => {
    const slot = { pizza, toppings: [], toppingsTotal: 0 };
    if (step === 0) {
      setSlot1(slot);
      setTimeout(() => setStep(1), 200);
    } else {
      setSlot2(slot);
      setTimeout(() => setStep(2), 200);
    }
  };

  const finalizeCustomization = ({ pizza, toppings, toppingsTotal }) => {
    const slot = { pizza, toppings, toppingsTotal };
    if (customizing?.forSlot === 1) {
      setSlot1(slot);
      setTimeout(() => setStep(1), 200);
    } else {
      setSlot2(slot);
      setTimeout(() => setStep(2), 200);
    }
  };

  const toppingsLabel = (toppings) => toppings.map((t) => t.qty > 1 ? `${t.name} x${t.qty}` : t.name).join(', ');
  const totalToppingUnits = (toppings) => toppings.reduce((s, t) => s + t.qty, 0);

  const handleConfirm = () => {
    if (!slot1 || !slot2) return;
    const stamp = Date.now();
    // Every cart line gets `${menuId}-${stamp}<4-digit serial>`. The trailing
    // 17+ digits match the server's `-\d{13,}$` strip regex so the server can
    // resolve each line back to its menu doc.
    let serial = 1000;
    const nextSuffix = () => `${stamp}${serial++}`;
    const bundleId = `bogo-${stamp}`;

    [slot1, slot2].forEach((slot, i) => {
      const { pizza, toppings, toppingsTotal } = slot;
      const hasToppings = toppings.length > 0;
      onAddToCart({
        ...pizza,
        id: `${pizza.id}-${nextSuffix()}`,
        name: hasToppings ? `${pizza.name} (+ ${toppingsLabel(toppings)})` : pizza.name,
        price: pizza.price + toppingsTotal,
        _bundleId: bundleId,
        _bundleType: 'bogo',
        _bundleSlot: i + 1,
        _bundleToppings: toppings,
      });
    });

    // Sides — drinks + extras the customer added in steps 2/3, at full price.
    Object.entries(sides).forEach(([itemId, qty]) => {
      const item = findSideItem(itemId);
      if (!item) return;
      for (let q = 0; q < qty; q++) {
        onAddToCart({
          ...item,
          id: `${item.id}-${nextSuffix()}`,
          _bundleId: bundleId,
          _bundleType: 'bogo',
          _bundleSlot: 'side',
        });
      }
    });

    const discountAmount = Math.max(0, (slot1.pizza.price + slot2.pizza.price) - BOGO_TOTAL_PRICE);
    onAddToCart({
      id: `bogo-discount-${stamp}`,
      name: 'Buy One Get One Free Discount',
      price: -discountAmount,
      quantity: 1,
      isDiscount: true,
      _bundleId: bundleId,
      _bundleType: 'bogo',
      _bundleName: 'Buy One Get One Free',
    });
    if (onEditDone) onEditDone();
    onBack();
  };

  const stepLabels = ['Choose Pizza 1', 'Choose Pizza 2', 'Want any drinks?', 'Want any extras?'];
  const totalToppings = (slot1 ? totalToppingUnits(slot1.toppings) : 0) + (slot2 ? totalToppingUnits(slot2.toppings) : 0);
  const finalTotal = BOGO_TOTAL_PRICE + totalToppings * BOGO_TOPPING_PRICE + sidesTotal;

  return (
    <PageBackground>
      <AppHeader
        user={user}
        onHome={onBack}
        onShowProfile={onShowProfile}
        onSignIn={onSignIn}
        onShowCart={onShowCart}
        cartCount={cartCount}
      />

      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="bg-white bg-opacity-95 backdrop-blur-md rounded-2xl shadow-2xl overflow-hidden mb-6">
          <div className="text-white p-6" style={{ backgroundColor: MARINARA }}>
            <p className="text-sm" style={{ color: '#f0d4d2' }}>Buy One Get One Free</p>
            <p className="text-2xl font-bold">Any 2 pizzas for just £{BOGO_TOTAL_PRICE.toFixed(2)}</p>
            <h3 className="text-base font-semibold mt-2 mb-1" style={{ color: '#f0d4d2' }}>
              {step === BOGO_REVIEW_STEP ? 'Confirm your order' : stepLabels[step]}
            </h3>
            <WizardProgress current={step} total={BOGO_STEPS} />
          </div>
        </div>

        {step === 0 && (
          <>
            {slot1 && (
              <div className="mb-4 bg-white bg-opacity-95 backdrop-blur-md rounded-xl shadow-lg p-4 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-500">Current pizza 1</p>
                  <p className="font-semibold text-gray-800 truncate">
                    {slot1.pizza.name}
                    {slot1.toppings.length > 0 && <span className="text-gray-500 font-normal"> (+ {toppingsLabel(slot1.toppings)})</span>}
                  </p>
                </div>
                <button onClick={() => setStep(1)}
                  className="text-white px-4 py-2 rounded-lg font-semibold transition-colors flex-shrink-0"
                  style={{ backgroundColor: MARINARA }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = MARINARA_DARK}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = MARINARA}>
                  Keep
                </button>
              </div>
            )}
            <PizzaGrid pizzas={allPizzas} onAdd={quickAdd} onCustomize={startCustomize} />
          </>
        )}

        {step === 1 && (
          <>
            {slot1 && (
              <SelectionsSummary
                items={[{ label: 'Pizza 1', name: slot1.pizza.name, toppings: slot1.toppings }]}
              />
            )}
            {slot2 && (
              <div className="mb-4 bg-white bg-opacity-95 backdrop-blur-md rounded-xl shadow-lg p-4 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-500">Current pizza 2</p>
                  <p className="font-semibold text-gray-800 truncate">
                    {slot2.pizza.name}
                    {slot2.toppings.length > 0 && <span className="text-gray-500 font-normal"> (+ {toppingsLabel(slot2.toppings)})</span>}
                  </p>
                </div>
                <button onClick={() => setStep(2)}
                  className="text-white px-4 py-2 rounded-lg font-semibold transition-colors flex-shrink-0"
                  style={{ backgroundColor: MARINARA }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = MARINARA_DARK}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = MARINARA}>
                  Keep
                </button>
              </div>
            )}
            <p className="text-white text-2xl font-bold mb-3 drop-shadow">Pick a second pizza — same or different.</p>
            <PizzaGrid pizzas={allPizzas} onAdd={quickAdd} onCustomize={startCustomize} />
            <button onClick={() => setStep(0)} className="mt-4 text-sm text-white drop-shadow hover:underline flex items-center gap-1">
              <ChevronLeft size={16} /> Change first pizza
            </button>
          </>
        )}

        {/* Step 2 — drinks (optional). Step 3 — extras (starters/desserts/dips, optional). */}
        {(step === 2 || step === 3) && (() => {
          const groups = step === 2
            ? [{ label: 'Soft Drinks', items: drinks }]
            : [
                { label: 'Starters', items: starters },
                { label: 'Desserts', items: desserts },
                { label: 'Dips', items: dipItems },
              ];
          const hasAny = groups.some((g) => g.items.length > 0);
          const priorPicks = [];
          if (slot1) priorPicks.push({ label: 'Pizza 1', name: slot1.pizza.name, toppings: slot1.toppings });
          if (slot2) priorPicks.push({ label: 'Pizza 2', name: slot2.pizza.name, toppings: slot2.toppings });
          if (step === 3) {
            Object.entries(sides).forEach(([id, qty]) => {
              const drink = drinks.find((d) => d.id === id);
              if (!drink) return;
              priorPicks.push({ label: 'Drink', name: qty > 1 ? `${drink.name} x${qty}` : drink.name });
            });
          }
          return (
            <div className="max-w-2xl mx-auto">
              <SelectionsSummary items={priorPicks} />
              <p className="text-white text-sm mb-3 drop-shadow">
                {step === 2 ? 'Add any drinks at menu price, or skip ahead.' : 'Add any starters, desserts or dips, or skip.'}
              </p>
              <div className="bg-white bg-opacity-95 backdrop-blur-md rounded-2xl shadow-2xl overflow-hidden">
                <div className="p-4">
                  {!hasAny && (
                    <p className="text-center text-gray-400 py-6 text-sm">Nothing available right now — tap continue.</p>
                  )}
                  {groups.filter((g) => g.items.length > 0).map((g, gi) => (
                    <div key={g.label} className={gi > 0 ? 'mt-4' : ''}>
                      <h4 className="font-bold text-gray-800 mb-2 pb-1 border-b">{g.label}</h4>
                      <div className="divide-y divide-gray-100">
                        {g.items.map((item) => {
                          const qty = sides[item.id] || 0;
                          return (
                            <div key={item.id} className="flex items-center justify-between gap-3 py-2.5">
                              <div className="min-w-0 flex-1">
                                <p className="font-semibold text-gray-800 text-sm">{item.name}</p>
                                {item.description && <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{item.description}</p>}
                              </div>
                              <span className="text-sm font-bold flex-shrink-0" style={{ color: MARINARA }}>£{item.price.toFixed(2)}</span>
                              <div className="flex items-center gap-1.5 flex-shrink-0">
                                <button
                                  type="button"
                                  onClick={() => decSide(item.id)}
                                  disabled={qty === 0}
                                  aria-label={`Remove one ${item.name}`}
                                  className="w-7 h-7 rounded-full flex items-center justify-center font-bold text-base transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                                  style={{ backgroundColor: qty === 0 ? '#e5e7eb' : MARINARA, color: qty === 0 ? '#9ca3af' : '#fff' }}>−</button>
                                <span className="w-5 text-center font-bold text-gray-800 text-sm">{qty}</span>
                                <button
                                  type="button"
                                  onClick={() => incSide(item.id)}
                                  aria-label={`Add one ${item.name}`}
                                  className="w-7 h-7 rounded-full flex items-center justify-center font-bold text-base text-white transition-all"
                                  style={{ backgroundColor: MARINARA }}>+</button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex gap-3 mt-4">
                <button onClick={() => setStep(step - 1)}
                  className="flex-1 py-3 rounded-xl font-semibold border-2 transition-colors"
                  style={{ borderColor: 'rgba(255,255,255,0.6)', color: '#fff', backgroundColor: 'transparent' }}>
                  Back
                </button>
                <button onClick={() => setStep(step + 1)}
                  className="flex-1 text-white py-3 rounded-xl font-bold transition-colors"
                  style={{ backgroundColor: MARINARA }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = MARINARA_DARK}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = MARINARA}>
                  {step === 2 ? 'Continue' : 'Review order'}
                </button>
              </div>
            </div>
          );
        })()}

        {step === BOGO_REVIEW_STEP && (
          <div className="max-w-lg mx-auto">
            <div className="bg-white bg-opacity-95 backdrop-blur-md rounded-2xl shadow-2xl overflow-hidden">
              <div className="p-6 space-y-4">
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <h4 className="font-bold text-gray-800 mb-3">Your Buy One Get One Free Order</h4>
                  <div className="space-y-2 text-sm">
                    {[slot1, slot2].map((slot, i) => slot && (
                      <div key={i}>
                        <div className="font-medium text-gray-800">{slot.pizza.name}</div>
                        {slot.toppings.map((t) => (
                          <div key={t.id} className="text-xs text-gray-500 pl-3">
                            + {t.name}{t.qty > 1 ? ` x${t.qty}` : ''}
                          </div>
                        ))}
                      </div>
                    ))}
                    <div className="flex justify-between text-gray-700 border-t pt-2">
                      <span>Bundle base</span>
                      <span>£{BOGO_TOTAL_PRICE.toFixed(2)}</span>
                    </div>
                    {totalToppings > 0 && (
                      <div className="flex justify-between text-gray-700">
                        <span>Toppings ({totalToppings} × £{BOGO_TOPPING_PRICE.toFixed(2)})</span>
                        <span>£{(totalToppings * BOGO_TOPPING_PRICE).toFixed(2)}</span>
                      </div>
                    )}
                    {totalSideUnits > 0 && (
                      <>
                        <div className="border-t pt-1 mt-1 font-medium text-gray-800">Extras</div>
                        {Object.entries(sides).map(([id, qty]) => {
                          const item = findSideItem(id);
                          if (!item) return null;
                          return (
                            <div key={id} className="flex justify-between text-xs text-gray-600 pl-3">
                              <span>{item.name}{qty > 1 ? ` x${qty}` : ''}</span>
                              <span>£{(item.price * qty).toFixed(2)}</span>
                            </div>
                          );
                        })}
                      </>
                    )}
                    <div className="border-t pt-2 flex justify-between font-bold text-base">
                      <span>You pay</span>
                      <span style={{ color: MARINARA }}>£{finalTotal.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
                <button onClick={handleConfirm} className="w-full text-white py-4 rounded-xl font-bold transition-colors" style={{ backgroundColor: MARINARA }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = MARINARA_DARK}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = MARINARA}>
                  Add to Cart — £{finalTotal.toFixed(2)}
                </button>
                <button onClick={() => setStep(3)} className="w-full text-gray-500 py-2 text-sm hover:text-gray-700 flex items-center justify-center gap-1">
                  <ChevronLeft size={16} /> Make changes
                </button>
              </div>
            </div>
          </div>
        )}
      </main>

      {customizing && (
        <PizzaCustomizer
          pizza={customizing.pizza}
          menuItems={menuItems}
          bogoMode
          onConfirm={finalizeCustomization}
          onClose={() => setCustomizing(null)}
        />
      )}
    </PageBackground>
  );
};

// ─── Meal Deals data ─────────────────────────────────────────────────────────
// Each deal has a fixed totalPrice. The deal discount line added to the cart
// is computed at confirm time as (sum of selected items at regular price) − totalPrice.
const MEAL_DEALS = [
  {
    id: 'deal1', name: 'Meal Deal for 1', description: '1 Pizza + 1 Starter + 1 Drink + 1 Dip', totalPrice: 19.99,
    image: '/images/deal1.jpeg',
    slots: [
      { key: 'pizza', label: 'Choose your Pizza', categories: ['pizza12', 'pizza14'] },
      { key: 'starter', label: 'Choose your Starter', categories: ['starters'] },
      { key: 'drink', label: 'Choose your Drink', categories: ['softDrinks'] },
      { key: 'dip', label: 'Choose your Dip', categories: ['dips'] },
    ],
  },
  {
    id: 'deal2', name: 'Meal Deal for 2', description: '2 Pizzas + 2 Drinks + 1 Dip', totalPrice: 22.99,
    image: '/images/deal2.jpeg',
    slots: [
      { key: 'pizza1', label: 'Choose Pizza 1', categories: ['pizza12', 'pizza14'] },
      { key: 'pizza2', label: 'Choose Pizza 2', categories: ['pizza12', 'pizza14'] },
      { key: 'drink1', label: 'Choose Drink 1', categories: ['softDrinks'] },
      { key: 'drink2', label: 'Choose Drink 2', categories: ['softDrinks'] },
      { key: 'dip', label: 'Choose your Dip', categories: ['dips'] },
    ],
  },
  {
    id: 'deal3', name: 'Meal Deal for 2 (with Starter)', description: '2 Pizzas + 1 Starter + 2 Drinks + 1 Dip', totalPrice: 26.99,
    image: '/images/deal3.jpeg',
    slots: [
      { key: 'pizza1', label: 'Choose Pizza 1', categories: ['pizza12', 'pizza14'] },
      { key: 'pizza2', label: 'Choose Pizza 2', categories: ['pizza12', 'pizza14'] },
      { key: 'starter', label: 'Choose your Starter', categories: ['starters'] },
      { key: 'drink1', label: 'Choose Drink 1', categories: ['softDrinks'] },
      { key: 'drink2', label: 'Choose Drink 2', categories: ['softDrinks'] },
      { key: 'dip', label: 'Choose your Dip', categories: ['dips'] },
    ],
  },
  {
    id: 'deal4', name: 'Meal Deal for 3', description: '3 Pizzas + 3 Drinks + 1 Dip', totalPrice: 29.99,
    image: '/images/deal4.jpeg',
    slots: [
      { key: 'pizza1', label: 'Choose Pizza 1', categories: ['pizza12', 'pizza14'] },
      { key: 'pizza2', label: 'Choose Pizza 2', categories: ['pizza12', 'pizza14'] },
      { key: 'pizza3', label: 'Choose Pizza 3', categories: ['pizza12', 'pizza14'] },
      { key: 'drink1', label: 'Choose Drink 1', categories: ['softDrinks'] },
      { key: 'drink2', label: 'Choose Drink 2', categories: ['softDrinks'] },
      { key: 'drink3', label: 'Choose Drink 3', categories: ['softDrinks'] },
      { key: 'dip', label: 'Choose your Dip', categories: ['dips'] },
    ],
  },
  {
    id: 'deal5', name: 'Meal Deal for 3 (with Starters)', description: '3 Pizzas + 2 Starters + 1 Drink + 1 Dip', totalPrice: 39.99,
    image: '/images/deal5.jpeg',
    slots: [
      { key: 'pizza1', label: 'Choose Pizza 1', categories: ['pizza12', 'pizza14'] },
      { key: 'pizza2', label: 'Choose Pizza 2', categories: ['pizza12', 'pizza14'] },
      { key: 'pizza3', label: 'Choose Pizza 3', categories: ['pizza12', 'pizza14'] },
      { key: 'starter1', label: 'Choose Starter 1', categories: ['starters'] },
      { key: 'starter2', label: 'Choose Starter 2', categories: ['starters'] },
      { key: 'drink', label: 'Choose your Drink', categories: ['softDrinks'] },
      { key: 'dip', label: 'Choose your Dip', categories: ['dips'] },
    ],
  },
];

// ─── Meal Deal Page (full screen) ────────────────────────────────────────────
// Selections shape:
//   pizza slots → { _isPizza: true, pizza, toppings: [{id,name,price,qty}], toppingsTotal }
//   other slots → the raw item itself
// Bundle math: dealTotal covers all base items; each topping adds a flat £1.
const MealDealPage = ({ menuItems, onBack, onAddToCart, user, onSignIn, onShowProfile, onShowCart, cartCount, editingBundle, onEditDone }) => {
  // Reconstruct meal-deal state from a prior bundle, if editing.
  const restoreFromBundle = () => {
    if (!editingBundle || editingBundle.bundleType !== 'mealdeal' || !editingBundle.dealId) {
      return { deal: null, sels: {} };
    }
    const deal = MEAL_DEALS.find((d) => d.id === editingBundle.dealId) || null;
    const sels = {};
    editingBundle.items
      .filter((c) => !c.isDiscount && c._bundleSlot)
      .forEach((c) => {
        const baseId = String(c.id).replace(/-\d{13,}$/, '');
        const isPizza = (c.category === 'pizza12' || c.category === 'pizza14');
        if (isPizza) {
          const toppings = c._bundleToppings || [];
          const toppingsTotal = toppings.reduce((s, t) => s + t.qty * BOGO_TOPPING_PRICE, 0);
          const pizza = { ...c, id: baseId, name: c.name.replace(/ \(\+ .+\)$/, ''), price: c.price - toppingsTotal };
          sels[c._bundleSlot] = { _isPizza: true, pizza, toppings, toppingsTotal };
        } else {
          sels[c._bundleSlot] = { ...c, id: baseId };
        }
      });
    return { deal, sels };
  };
  const { deal: initialDeal, sels: initialSels } = restoreFromBundle();

  // When editing, drop the user at the start of the build flow with selections
  // prefilled. Each slot offers a "Keep" button to skip past unchanged choices.
  const [phase, setPhase] = useState(initialDeal ? 'build' : 'select');
  const [activeDeal, setActiveDeal] = useState(initialDeal);
  const [selections, setSelections] = useState(initialSels);
  const [step, setStep] = useState(0);
  const [customizing, setCustomizing] = useState(null); // { pizza, slotKey }

  // Meal deals only include canned (330ml) drinks — big bottles are excluded.
  const isCannedDrink = (item) => /330\s*ml|\bcan(s|ned)?\b/i.test(`${item.name || ''} ${item.description || ''}`);
  const itemsForCategory = (cat) => {
    const list = menuItems[cat] || [];
    return cat === 'softDrinks' ? list.filter(isCannedDrink) : list;
  };

  const activeSlots = activeDeal
    ? activeDeal.slots.filter((slot) => slot.categories.some((cat) => itemsForCategory(cat).length > 0))
    : [];

  const getItemsForSlot = (slot) => slot.categories.flatMap((cat) => itemsForCategory(cat));
  const currentSlot = activeSlots[step];
  const currentSlotIsPizza = !!currentSlot?.categories.some((c) => c === 'pizza12' || c === 'pizza14');

  // Selection helpers — work for both pizza and non-pizza shapes.
  const selBasePrice = (sel) => sel?._isPizza ? (sel.pizza?.price || 0) : (sel?.price || 0);
  const selName = (sel) => sel?._isPizza ? sel.pizza?.name : sel?.name;
  const selToppingUnits = (sel) => sel?._isPizza ? sel.toppings.reduce((s, t) => s + t.qty, 0) : 0;
  const toppingsLabel = (toppings) => toppings.map((t) => t.qty > 1 ? `${t.name} x${t.qty}` : t.name).join(', ');

  const advanceStep = () => setTimeout(() => {
    if (step + 1 < activeSlots.length) setStep(step + 1);
    else setPhase('review');
  }, 200);

  const selectPlainItem = (item) => {
    if (!currentSlot) return;
    setSelections((prev) => ({ ...prev, [currentSlot.key]: item }));
    advanceStep();
  };

  const addPizzaPlain = (pizza) => {
    if (!currentSlot) return;
    setSelections((prev) => ({
      ...prev,
      [currentSlot.key]: { _isPizza: true, pizza, toppings: [], toppingsTotal: 0 },
    }));
    advanceStep();
  };

  const startPizzaCustomize = (pizza) => {
    if (!currentSlot) return;
    setCustomizing({ pizza, slotKey: currentSlot.key });
  };

  const finalizeCustomization = ({ pizza, toppings, toppingsTotal }) => {
    if (!customizing) return;
    setSelections((prev) => ({
      ...prev,
      [customizing.slotKey]: { _isPizza: true, pizza, toppings, toppingsTotal },
    }));
    advanceStep();
  };

  const startDeal = (deal) => {
    setActiveDeal(deal);
    setSelections({});
    setStep(0);
    setPhase('build');
  };

  const getSubtotal = () => Object.values(selections).reduce((sum, sel) => sum + selBasePrice(sel), 0);
  const totalToppingUnits = Object.values(selections).reduce((s, sel) => s + selToppingUnits(sel), 0);
  const getDealTotal = () => activeDeal ? activeDeal.totalPrice : 0;
  const getDealDiscount = () => Math.max(0, getSubtotal() - getDealTotal());
  const finalTotal = getDealTotal() + totalToppingUnits * BOGO_TOPPING_PRICE;

  const handleAddDeal = () => {
    const stamp = Date.now();
    const bundleId = `mealdeal-${activeDeal.id}-${stamp}`;
    // Build a flat list of slot entries with a stable index so we can give each
    // a unique cart ID of the shape `${menuId}-<13+ digits>` that the server can parse.
    const entries = Object.entries(selections);
    entries.forEach(([key, sel], i) => {
      const suffix = `${stamp}${i + 1}`;
      if (sel?._isPizza) {
        const { pizza, toppings, toppingsTotal } = sel;
        const hasToppings = toppings.length > 0;
        onAddToCart({
          ...pizza,
          id: `${pizza.id}-${suffix}`,
          name: hasToppings ? `${pizza.name} (+ ${toppingsLabel(toppings)})` : pizza.name,
          price: pizza.price + toppingsTotal,
          _bundleId: bundleId,
          _bundleType: 'mealdeal',
          _bundleSlot: key,
          _bundleToppings: toppings,
        });
      } else {
        onAddToCart({
          ...sel,
          // Unique ID prevents merging with a separately-added drink/starter/dip in cart.
          id: `${sel.id}-${suffix}`,
          _bundleId: bundleId,
          _bundleType: 'mealdeal',
          _bundleSlot: key,
        });
      }
    });
    const discountAmount = getDealDiscount();
    onAddToCart({
      id: `discount-${activeDeal.id}-${stamp}`,
      name: `${activeDeal.name} Discount`,
      price: -discountAmount,
      quantity: 1,
      isDiscount: true,
      _bundleId: bundleId,
      _bundleType: 'mealdeal',
      _bundleName: activeDeal.name,
      _bundleDealId: activeDeal.id,
    });
    if (onEditDone) onEditDone();
    onBack();
  };

  return (
    <PageBackground>
      <AppHeader
        user={user}
        onHome={onBack}
        onShowProfile={onShowProfile}
        onSignIn={onSignIn}
        onShowCart={onShowCart}
        cartCount={cartCount}
      />

      <main className="max-w-7xl mx-auto px-4 py-8">
        {phase === 'select' && (
          <>
            <div className="mb-6 bg-white bg-opacity-95 backdrop-blur-md rounded-2xl shadow-2xl overflow-hidden">
              <div className="text-white p-6" style={{ backgroundColor: MARINARA }}>
                <h3 className="text-2xl font-bold">Meal Deals</h3>
                <p className="text-sm mt-1" style={{ color: '#f0d4d2' }}>Save money, eat more</p>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {MEAL_DEALS.map((deal) => (
                <div key={deal.id} className="bg-white rounded-lg shadow-md overflow-hidden flex flex-col">
                  {deal.image && (
                    <div className="zoom-wrap w-full h-48">
                      <img src={deal.image} alt={deal.name} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="p-5 flex flex-col flex-1">
                    <h4 className="text-xl font-bold mb-1">{deal.name}</h4>
                    <p className="text-gray-600 text-sm mb-4 italic flex-1">{deal.description}</p>
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-xl font-bold" style={{ color: MARINARA }}>£{deal.totalPrice.toFixed(2)}</span>
                      <button
                        onClick={() => startDeal(deal)}
                        className="text-white px-4 py-2 rounded-lg font-semibold transition-colors"
                        style={{ backgroundColor: MARINARA }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = MARINARA_DARK}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = MARINARA}>
                        Choose
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {phase === 'build' && (
          <>
            <div className="mb-6 bg-white bg-opacity-95 backdrop-blur-md rounded-2xl shadow-2xl overflow-hidden">
              <div className="text-white p-6" style={{ backgroundColor: MARINARA }}>
                <p className="text-sm mb-1" style={{ color: '#f0d4d2' }}>{activeDeal.name}</p>
                <h3 className="text-xl font-bold">{currentSlot?.label}</h3>
                <p className="text-sm mt-1" style={{ color: '#f0d4d2' }}>Step {step + 1} of {activeSlots.length}</p>
                <WizardProgress current={step} total={activeSlots.length} />
              </div>
            </div>

            <SelectionsSummary
              items={activeSlots.slice(0, step)
                .map((slot) => {
                  const sel = selections[slot.key];
                  if (!sel) return null;
                  return {
                    label: slot.label.replace(/^Choose (your )?/i, ''),
                    name: selName(sel),
                    toppings: sel?._isPizza ? sel.toppings : [],
                  };
                })
                .filter(Boolean)}
            />

            {/* If this slot already has a selection (editing), show a Keep banner. */}
            {currentSlot && selections[currentSlot.key] && (() => {
              const sel = selections[currentSlot.key];
              const tops = sel?._isPizza ? sel.toppings : [];
              return (
                <div className="mb-4 bg-white bg-opacity-95 backdrop-blur-md rounded-xl shadow-lg p-4 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-500">Currently selected</p>
                    <p className="font-semibold text-gray-800 truncate">
                      {selName(sel)}
                      {tops.length > 0 && <span className="text-gray-500 font-normal"> (+ {toppingsLabel(tops)})</span>}
                    </p>
                  </div>
                  <button onClick={advanceStep}
                    className="text-white px-4 py-2 rounded-lg font-semibold transition-colors flex-shrink-0"
                    style={{ backgroundColor: MARINARA }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = MARINARA_DARK}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = MARINARA}>
                    Keep
                  </button>
                </div>
              );
            })()}

            {(() => {
              const items = currentSlot ? getItemsForSlot(currentSlot) : [];
              if (items.length === 0) {
                return (
                  <div className="bg-white bg-opacity-95 rounded-2xl p-8 text-center text-gray-400">
                    No items available in this category.
                  </div>
                );
              }
              // Pizza steps reuse the Full-Menu-style grid with Add + Customize.
              if (currentSlotIsPizza) {
                return <PizzaGrid pizzas={items} onAdd={addPizzaPlain} onCustomize={startPizzaCustomize} />;
              }
              // Other categories stay as a compact list (drinks have no images, etc).
              return (
                <div className="bg-white bg-opacity-95 backdrop-blur-md rounded-2xl shadow-2xl overflow-hidden">
                  <div className="p-4 space-y-2">
                    {items.map((item) => (
                      <div key={item.id} onClick={() => selectPlainItem(item)}
                        className="flex items-center justify-between p-3 rounded-xl border-2 bg-gray-50 cursor-pointer transition-all"
                        style={{ borderColor: '#f3f4f6' }}
                        onMouseEnter={(e) => e.currentTarget.style.borderColor = MARINARA}
                        onMouseLeave={(e) => e.currentTarget.style.borderColor = '#f3f4f6'}>
                        <div className="flex items-center gap-3">
                          {item.image && (
                            <div className="zoom-wrap w-12 h-12 rounded-lg flex-shrink-0">
                              <img src={item.image} alt={item.name} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                            </div>
                          )}
                          <div>
                            <p className="font-medium text-gray-800 text-sm">{item.name}</p>
                            {item.description && <p className="text-xs text-gray-500">{item.description}</p>}
                          </div>
                        </div>
                        {/* Price intentionally hidden — meal deal price is fixed. */}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            <div className="mt-4">
              {step > 0 ? (
                <button onClick={() => setStep(step - 1)} className="text-white text-sm drop-shadow hover:underline flex items-center gap-1">
                  <ChevronLeft size={16} /> Back
                </button>
              ) : (
                <button onClick={() => setPhase('select')} className="text-white text-sm drop-shadow hover:underline flex items-center gap-1">
                  <ChevronLeft size={16} /> Change deal
                </button>
              )}
            </div>
          </>
        )}

        {phase === 'review' && (
          <div className="max-w-lg mx-auto">
            <div className="bg-white bg-opacity-95 backdrop-blur-md rounded-2xl shadow-2xl overflow-hidden">
              <div className="text-white p-6" style={{ backgroundColor: MARINARA }}>
                <p className="text-sm mb-1" style={{ color: '#f0d4d2' }}>Review your deal</p>
                <h3 className="text-xl font-bold">{activeDeal.name}</h3>
              </div>
              <div className="p-6 space-y-3">
                {activeSlots.map((slot) => {
                  const sel = selections[slot.key];
                  if (!sel) return null;
                  const tops = sel?._isPizza ? sel.toppings : [];
                  return (
                    <div key={slot.key} className="p-3 bg-gray-50 rounded-xl">
                      <p className="text-xs text-gray-500">{slot.label}</p>
                      <p className="font-medium text-gray-800">{selName(sel)}</p>
                      {tops.length > 0 && (
                        <div className="mt-1 pl-3 space-y-0.5">
                          {tops.map((t) => (
                            <div key={t.id} className="flex justify-between text-xs text-gray-500">
                              <span>+ {t.name}{t.qty > 1 ? ` x${t.qty}` : ''}</span>
                              <span>£{(t.qty * BOGO_TOPPING_PRICE).toFixed(2)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}

                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mt-4">
                  <div className="flex justify-between text-sm text-gray-700"><span>{activeDeal.name}</span><span>£{getDealTotal().toFixed(2)}</span></div>
                  {totalToppingUnits > 0 && (
                    <div className="flex justify-between text-sm text-gray-700 mt-1">
                      <span>Toppings ({totalToppingUnits} × £{BOGO_TOPPING_PRICE.toFixed(2)})</span>
                      <span>£{(totalToppingUnits * BOGO_TOPPING_PRICE).toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold text-lg border-t pt-2 mt-2"><span>You pay</span><span style={{ color: MARINARA }}>£{finalTotal.toFixed(2)}</span></div>
                </div>

                <button onClick={handleAddDeal}
                  className="w-full text-white py-4 rounded-xl font-bold transition-colors mt-4"
                  style={{ backgroundColor: MARINARA }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = MARINARA_DARK}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = MARINARA}>
                  Add Deal to Cart — £{finalTotal.toFixed(2)}
                </button>
                <button onClick={() => { setStep(activeSlots.length - 1); setPhase('build'); }}
                  className="w-full text-gray-500 py-2 text-sm hover:text-gray-700">
                  ← Make changes
                </button>
              </div>
            </div>
          </div>
        )}
      </main>

      {customizing && (
        <PizzaCustomizer
          pizza={customizing.pizza}
          menuItems={menuItems}
          bogoMode
          onConfirm={finalizeCustomization}
          onClose={() => setCustomizing(null)}
        />
      )}
    </PageBackground>
  );
};

// ─── Home Screen ──────────────────────────────────────────────────────────────
// Page scrolls normally; bottom padding gives a small breathing-room margin
// under the last button so the user can pull a touch past Full Menu but
// doesn't land in a giant empty area.
const HomeScreen = ({ onNavigate, user, onSignIn, onShowProfile, onShowCart, cartCount = 0 }) => {
  return (
    <PageBackground overlayOpacity={0.55}>
      {/* Top bar — cart + account, both top right */}
      <div className="px-6 pb-2" style={{ paddingTop: 'calc(env(safe-area-inset-top) + 1rem)' }}>
        <div className="flex justify-end items-center gap-3 max-w-lg mx-auto">
          <button onClick={onShowCart}
            className="relative flex items-center gap-2 bg-white px-3 py-2 rounded-lg text-sm font-semibold shadow-md"
            style={{ color: MARINARA }}>
            <ShoppingCart size={16} />
            <span>{cartCount}</span>
          </button>
          {user ? (
            <button onClick={onShowProfile}
              className="flex items-center gap-2 bg-white bg-opacity-20 backdrop-blur-sm hover:bg-opacity-30 text-white px-3 py-2 rounded-lg text-sm border border-white border-opacity-30">
              <User size={16} /> {user.displayName || 'Account'}
            </button>
          ) : (
            <button onClick={onSignIn}
              className="flex items-center gap-2 bg-white px-3 py-2 rounded-lg text-sm font-semibold shadow-md"
              style={{ color: MARINARA }}>
              <User size={16} /> Sign In
            </button>
          )}
        </div>
      </div>

      {/* Hero — bouncing tomatoes animation + tagline */}
      <div className="px-6 pt-2 pb-4 text-center text-white">
        <div className="tomato-scene-wrap">
          <div className="tomato-scene">
            <img className="tomato tomato-boy" src="/images/tomato-boy.png?v=3" alt="" decoding="async" fetchpriority="high" />
            <img className="tomato tomato-girl" src="/images/tomato-girl.png?v=3" alt="" decoding="async" fetchpriority="high" />
          </div>
        </div>
        <p className="text-lg drop-shadow-lg tracking-wide mt-1">Handmade Sourdough Pizzas</p>
      </div>

      {/* Glass option boxes */}
      <div
        className="px-5 max-w-lg mx-auto w-full"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 2rem)' }}
      >
        <p className="text-white text-sm font-medium mb-4 text-center drop-shadow opacity-90">What are you after today?</p>

        <div className="space-y-4">
          <button onClick={() => onNavigate('bogo')}
            className="w-full bg-white bg-opacity-15 backdrop-blur-md rounded-2xl border border-white border-opacity-30 hover:bg-opacity-25 hover:border-opacity-60 active:scale-95 transition-all shadow-xl p-5 flex items-center gap-4 text-left">
            <div className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 bg-white bg-opacity-15 border border-white border-opacity-30">
              <Tag size={26} className="text-white" />
            </div>
            <div className="flex-1">
              <p className="text-white font-bold text-xl">Buy One Get One Free</p>
              <p className="text-white text-opacity-80 text-sm">Buy one pizza, get one free</p>
            </div>
            <ChevronRight size={22} className="text-white text-opacity-70 flex-shrink-0" />
          </button>

          <button onClick={() => onNavigate('mealdeals')}
            className="w-full bg-white bg-opacity-15 backdrop-blur-md rounded-2xl border border-white border-opacity-30 hover:bg-opacity-25 hover:border-opacity-60 active:scale-95 transition-all shadow-xl p-5 flex items-center gap-4 text-left">
            <div className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 bg-white bg-opacity-15 border border-white border-opacity-30">
              <UtensilsCrossed size={26} className="text-white" />
            </div>
            <div className="flex-1">
              <p className="text-white font-bold text-xl">Meal Deals</p>
              <p className="text-white text-opacity-80 text-sm">Pizza, sides &amp; drink bundles</p>
            </div>
            <ChevronRight size={22} className="text-white text-opacity-70 flex-shrink-0" />
          </button>

          <button onClick={() => onNavigate('menu')}
            className="w-full bg-white bg-opacity-15 backdrop-blur-md rounded-2xl border border-white border-opacity-30 hover:bg-opacity-25 hover:border-opacity-60 active:scale-95 transition-all shadow-xl p-5 flex items-center gap-4 text-left">
            <div className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 bg-white bg-opacity-15 border border-white border-opacity-30">
              <MenuSquare size={26} className="text-white" />
            </div>
            <div className="flex-1">
              <p className="text-white font-bold text-xl">Full Menu</p>
              <p className="text-white text-opacity-80 text-sm">Browse all pizzas, sides &amp; drinks</p>
            </div>
            <ChevronRight size={22} className="text-white text-opacity-70 flex-shrink-0" />
          </button>
        </div>
      </div>
    </PageBackground>
  );
};

// ─── App Download Page ───────────────────────────────────────────────────────
// Rendered for pomopizza.uk/app. Two store buttons (Google Play + App Store)
// with a "coming soon" message at the bottom directing users to the website.
const AppDownloadPage = () => {
  const [androidStepsOpen, setAndroidStepsOpen] = useState(false);

  const handleAndroidClick = (e) => {
    e.preventDefault();
    setAndroidStepsOpen((open) => !open);
  };

  return (
    <PageBackground overlayOpacity={0.6}>
      <div className="min-h-screen flex flex-col">
        <div
          className="px-6"
          style={{ paddingTop: 'calc(env(safe-area-inset-top) + 2.5rem)' }}
        >
          <div className="max-w-lg mx-auto text-center">
            <h1 className="text-white text-4xl sm:text-5xl font-bold drop-shadow-lg mb-3">
              Get the App
            </h1>
            <p className="text-white text-opacity-90 text-lg drop-shadow">
              Order PomoPizza right from your phone
            </p>
          </div>
        </div>

        <div className="flex-1 px-5 max-w-lg mx-auto w-full flex flex-col justify-center gap-4 py-10">
          <button
            type="button"
            onClick={handleAndroidClick}
            className="w-full bg-white rounded-2xl shadow-xl p-5 flex items-center gap-4 hover:bg-opacity-95 active:scale-95 transition-all"
          >
            <div
              className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: MARINARA }}
            >
              {/* Google Play triangle */}
              <svg viewBox="0 0 24 24" width="28" height="28" fill="white" aria-hidden="true">
                <path d="M3.5 2.1l13.6 9.9-13.6 9.9c-.3-.3-.5-.7-.5-1.2V3.3c0-.5.2-.9.5-1.2zM18.7 13.4l2.6-1.4c.9-.5.9-1.5 0-2l-2.6-1.4-3 2.4 3 2.4z" />
              </svg>
            </div>
            <div className="flex-1 text-left">
              <p className="text-xs uppercase tracking-wide" style={{ color: '#666' }}>
                Test it on
              </p>
              <p className="text-xl font-bold" style={{ color: MARINARA }}>
                Google Play
              </p>
            </div>
            <ChevronRight
              size={22}
              style={{
                color: MARINARA,
                transform: androidStepsOpen ? 'rotate(90deg)' : 'none',
                transition: 'transform 0.2s',
              }}
              className="flex-shrink-0"
            />
          </button>

          {androidStepsOpen && (
            <div className="bg-white rounded-2xl shadow-xl p-5">
              <p className="text-base font-bold mb-3" style={{ color: MARINARA }}>
                Test our app on Android
              </p>
              <ol className="space-y-3">
                <li className="flex gap-3">
                  <span
                    className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-white text-sm font-bold"
                    style={{ backgroundColor: MARINARA }}
                  >
                    1
                  </span>
                  <div className="flex-1 text-sm" style={{ color: '#333' }}>
                    <a
                      href="https://groups.google.com/g/pomopizza"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-semibold underline"
                      style={{ color: MARINARA }}
                    >
                      Join our Google Group
                    </a>{' '}
                    so we can add you as a tester.
                  </div>
                </li>
                <li className="flex gap-3">
                  <span
                    className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-white text-sm font-bold"
                    style={{ backgroundColor: MARINARA }}
                  >
                    2
                  </span>
                  <div className="flex-1 text-sm" style={{ color: '#333' }}>
                    Open the{' '}
                    <a
                      href="https://play.google.com/apps/testing/com.pomopizza.app"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-semibold underline"
                      style={{ color: MARINARA }}
                    >
                      tester opt-in link
                    </a>{' '}
                    and accept the invite.
                  </div>
                </li>
                <li className="flex gap-3">
                  <span
                    className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-white text-sm font-bold"
                    style={{ backgroundColor: MARINARA }}
                  >
                    3
                  </span>
                  <div className="flex-1 text-sm" style={{ color: '#333' }}>
                    Download PomoPizza from the Play Store and start testing!
                  </div>
                </li>
              </ol>
            </div>
          )}

          <a
            href="https://apps.apple.com/gb/app/pomopizza/id6776296651"
            target="_blank"
            rel="noopener noreferrer"
            className="w-full bg-white rounded-2xl shadow-xl p-5 flex items-center gap-4 hover:bg-opacity-95 active:scale-95 transition-all"
          >
            <div
              className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: MARINARA }}
            >
              {/* Apple logo */}
              <svg viewBox="0 0 24 24" width="28" height="28" fill="white" aria-hidden="true">
                <path d="M17.05 12.04c-.03-3.05 2.49-4.51 2.6-4.58-1.42-2.08-3.63-2.36-4.42-2.4-1.88-.19-3.67 1.11-4.63 1.11-.97 0-2.43-1.08-4-1.06-2.06.03-3.96 1.2-5.02 3.04-2.14 3.71-.55 9.21 1.54 12.23 1.02 1.48 2.24 3.14 3.84 3.08 1.54-.06 2.13-.99 3.99-.99 1.86 0 2.39.99 4.02.97 1.66-.03 2.71-1.5 3.72-2.99 1.17-1.71 1.66-3.39 1.68-3.48-.04-.02-3.22-1.23-3.32-4.93zM14.07 3.51c.84-1.03 1.41-2.45 1.25-3.88-1.21.05-2.69.81-3.56 1.83-.78.91-1.46 2.37-1.28 3.76 1.35.1 2.74-.69 3.59-1.71z" />
              </svg>
            </div>
            <div className="flex-1 text-left">
              <p className="text-xs uppercase tracking-wide" style={{ color: '#666' }}>
                Download on the
              </p>
              <p className="text-xl font-bold" style={{ color: MARINARA }}>
                App Store
              </p>
            </div>
            <ChevronRight size={22} style={{ color: MARINARA }} className="flex-shrink-0" />
          </a>
        </div>

        <div
          className="px-6 max-w-lg mx-auto w-full text-center"
          style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 2.5rem)' }}
        >
          <p className="text-white text-opacity-90 text-base drop-shadow mb-4 leading-relaxed">
            Prefer ordering on our website? Free delivery within 4 miles of our restaurant.
          </p>
          <a
            href="/"
            className="inline-block bg-white px-6 py-3 rounded-lg font-semibold shadow-md hover:bg-opacity-95 transition-colors"
            style={{ color: MARINARA }}
          >
            Order on the website
          </a>
        </div>
      </div>
    </PageBackground>
  );
};

// ─── Main App ─────────────────────────────────────────────────────────────────
const PomoPizzaApp = () => {
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  // When set, the BOGO or Meal Deal page reopens prefilled from this bundle's
  // cart items, and on confirm the new bundle items replace the old ones.
  // Shape: { bundleId, bundleType, dealId? }
  const [editingBundle, setEditingBundle] = useState(null);
  const [activeSection, setActiveSection] = useState('home');
  const [showCheckout, setShowCheckout] = useState(false);
  const [loading, setLoading] = useState(true);
  const [menuItemsFromDB, setMenuItemsFromDB] = useState([]);
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState('signin');
  const [pizzaToCustomize, setPizzaToCustomize] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', address: '', postcode: '' });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [addressPredictions, setAddressPredictions] = useState([]);
  const [showAddressDropdown, setShowAddressDropdown] = useState(false);
  const [checkingDistance, setCheckingDistance] = useState(false);
  const [outOfRange, setOutOfRange] = useState(false);
  const [distanceCheckFailed, setDistanceCheckFailed] = useState(false);
  const [maxDeliveryMiles, setMaxDeliveryMiles] = useState(4);
  const [paymentStep, setPaymentStep] = useState('details');
  const [clientSecret, setClientSecret] = useState('');
  const [currentOrderId, setCurrentOrderId] = useState('');
  const [serverTotal, setServerTotal] = useState(0);
  const [paymentError, setPaymentError] = useState('');
  const [saveDetails, setSaveDetails] = useState(true);
  const [showProfile, setShowProfile] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const autocompleteTimersRef = useRef({});

  useEffect(() => {
    console.log('[DEBUG] auth listener registered');
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      console.log('[DEBUG] onAuthStateChanged fired, user:', currentUser?.uid || 'null');
      setUser(currentUser);
      if (currentUser) {
        try {
          const snap = await getDoc(doc(db, 'users', currentUser.uid));
          if (snap.exists()) {
            const data = snap.data();
            setUserProfile(data);
            setFormData((prev) => ({
              ...prev,
              name: data.name || prev.name,
              email: data.email || prev.email,
              phone: data.phone || prev.phone,
              address: data.address || prev.address,
              postcode: data.postcode || prev.postcode,
            }));
          }
        } catch (err) { console.error('Error loading profile:', err); }
      } else {
        setUserProfile(null);
      }
      console.log('[DEBUG] setAuthReady(true)');
      setAuthReady(true);
    });
    return () => unsubscribe();
  }, []);

  // Initialize the native Stripe SDK once (only on iOS/Android Capacitor builds).
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    const key = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
    if (!key) {
      console.warn('Skipping native Stripe init: REACT_APP_STRIPE_PUBLISHABLE_KEY not set');
      return;
    }
    NativeStripe.initialize({ publishableKey: key }).catch((err) =>
      console.error('Native Stripe init failed', err)
    );
  }, []);

  useEffect(() => {
    const loadDataFromFirebase = async () => {
      console.log('[DEBUG] loadDataFromFirebase: starting menuItems fetch');
      try {
        const menuSnapshot = await getDocs(collection(db, 'menuItems'));
        console.log('[DEBUG] menuItems fetch resolved, docs:', menuSnapshot.size);
        const items = [];
        menuSnapshot.forEach((doc) => {
          const data = doc.data();
          items.push({ id: doc.id, ...data, price: parseFloat(data.price) || 0, image: data.image || '' });
        });
        items.sort((a, b) => (a.order ?? 9999) - (b.order ?? 9999));
        setMenuItemsFromDB(items);
      } catch (error) { console.error('[DEBUG] menuItems fetch FAILED:', error); }
      finally {
        console.log('[DEBUG] setLoading(false)');
        setLoading(false);
      }
    };
    loadDataFromFirebase();
  }, []);

  const UK_POSTCODE_RE = /[A-Z]{1,2}\d{1,2}[A-Z]?\s?\d[A-Z]{2}/i;

  const extractPostcode = (description) => {
    const match = description?.match(UK_POSTCODE_RE);
    return match ? match[0].toUpperCase() : '';
  };

  const stripPostcodeAndCountry = (description) => {
    if (!description) return '';
    return description
      .replace(UK_POSTCODE_RE, '')
      .replace(/,\s*UK\s*$/i, '')
      .replace(/,\s*,/g, ',')
      .replace(/,\s*$/, '')
      .trim();
  };

  const getAutocompletePredictions = async (input) => {
    if (!input || input.length < 2) {
      setAddressPredictions([]);
      setShowAddressDropdown(false);
      return;
    }
    try {
      const fn = httpsCallable(functions, 'getAutocompletePredictions');
      const result = await fn({ input });
      if (result.data?.predictions) {
        setAddressPredictions(result.data.predictions);
        setShowAddressDropdown(true);
      }
    } catch { setShowAddressDropdown(false); }
  };

  const handlePredictionSelect = (prediction) => {
    const postcode = extractPostcode(prediction.description);
    const addressLine = postcode ? stripPostcodeAndCountry(prediction.description) : prediction.description;
    setFormData((p) => ({ ...p, address: addressLine, postcode: postcode || p.postcode }));
    setShowAddressDropdown(false);
    setAddressPredictions([]);
    setFormErrors((p) => ({ ...p, address: '', postcode: '' }));
  };

  useEffect(() => {
    const addressOk = formData.address.trim().length >= 5;
    const postcodeOk = /^[A-Z]{1,2}\d{1,2}[A-Z]?\s?\d[A-Z]{2}$/i.test(formData.postcode.trim());
    if (!addressOk || !postcodeOk) {
      setOutOfRange(false);
      setDistanceCheckFailed(false);
      setCheckingDistance(false);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setCheckingDistance(true);
      try {
        const fn = httpsCallable(functions, 'calculateDistance');
        const result = await fn({
          customerAddress: formData.address.trim(),
          customerPostcode: formData.postcode.trim(),
        });
        if (cancelled) return;
        if (typeof result.data?.maxMiles === 'number') setMaxDeliveryMiles(result.data.maxMiles);
        if (result.data?.success === true) {
          setDistanceCheckFailed(false);
          setOutOfRange(result.data.withinRange !== true);
        } else {
          setDistanceCheckFailed(true);
          setOutOfRange(false);
          console.error('Distance check returned success=false', result.data);
        }
      } catch (err) {
        if (cancelled) return;
        setDistanceCheckFailed(true);
        setOutOfRange(false);
        console.error('Distance check threw', err);
      } finally {
        if (!cancelled) setCheckingDistance(false);
      }
    }, 800);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [formData.address, formData.postcode]);

  const sanitizeInput = (input) => input.replace(/[<>"']/g, '');
  const validateEmail = (email) => /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/.test(email);
  const validatePhone = (phone) => /^(\+44|0)[1-9]\d{9}$/.test(phone.replace(/\s/g, ''));
  const validatePostcode = (postcode) => /^[A-Z]{1,2}\d{1,2}[A-Z]?\s?\d[A-Z]{2}$/i.test(postcode.trim());
  const validateName = (name) => name.length >= 2 && name.length <= 50 && /^[a-zA-Z\s'-]+$/.test(name);
  const validateAddress = (address) => address.length >= 5 && address.length <= 200;

  const validateForm = () => {
    const errors = {};
    if (!validateName(formData.name)) errors.name = 'Please enter a valid name';
    if (!validateEmail(formData.email)) errors.email = 'Please enter a valid email';
    if (!validatePhone(formData.phone)) errors.phone = 'Please enter a valid UK phone number';
    if (!validateAddress(formData.address)) errors.address = 'Please enter a valid address';
    if (!validatePostcode(formData.postcode)) errors.postcode = 'Please enter a valid UK postcode';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (field, value) => {
    if (field === 'address') {
      setFormData((p) => ({ ...p, address: value }));
      if (autocompleteTimersRef.current.address) clearTimeout(autocompleteTimersRef.current.address);
      autocompleteTimersRef.current.address = setTimeout(() => getAutocompletePredictions(value), 300);
    } else if (field === 'postcode') {
      setFormData((p) => ({ ...p, postcode: value }));
    } else {
      setFormData((p) => ({ ...p, [field]: sanitizeInput(value) }));
    }
    if (formErrors[field]) setFormErrors((p) => ({ ...p, [field]: '' }));
  };

  const getMenuItems = () => {
    if (menuItemsFromDB.length > 0) {
      return {
        bogo: menuItemsFromDB.filter((i) => i.category === 'bogo'),
        starters: menuItemsFromDB.filter((i) => i.category === 'starters'),
        mealDeals: menuItemsFromDB.filter((i) => i.category === 'mealDeals'),
        pizza12: menuItemsFromDB.filter((i) => i.category === 'pizza12'),
        pizza14: menuItemsFromDB.filter((i) => i.category === 'pizza14'),
        softDrinks: menuItemsFromDB.filter((i) => i.category === 'softDrinks'),
        desserts: menuItemsFromDB.filter((i) => i.category === 'desserts'),
        dips: menuItemsFromDB.filter((i) => i.category === 'dips'),
        toppings: menuItemsFromDB.filter((i) => i.category === 'toppings'),
      };
    }
    return { bogo: [], starters: [], mealDeals: [], pizza12: [], pizza14: [], softDrinks: [], desserts: [], dips: [], toppings: [] };
  };

  const menuItems = getMenuItems();

  const categoryNames = {
    bogo: 'Buy One Get One Free',
    starters: 'Starters',
    mealDeals: 'Meal Deals',
    pizza12: '12" Handmade Slow Rising Sourdough Pizzas',
    pizza14: '14" Handmade Slow Rising Sourdough Pizzas',
    softDrinks: 'Soft Drinks',
    desserts: 'Classic Desserts',
    dips: 'Dips',
    toppings: 'Extra Toppings',
  };

  const handleAddToCartClick = (item) => {
    const cat = (item.category || '').trim().toLowerCase();
    const isPizza = cat === 'pizza12' || cat === 'pizza14';
    if (isPizza) setPizzaToCustomize(item);
    else addToCart(item);
  };

  const addToCart = (item) => {
    if (!item) return;
    setCart((prev) => {
      if (String(item.id).includes('-discount-') || String(item.id).match(/-\d{13,}$/)) {
        return [...prev, { ...item, quantity: 1 }];
      }
      const existing = prev.find((i) => i.id === item.id);
      if (existing) return prev.map((i) => (i.id === item.id ? { ...i, quantity: i.quantity + 1 } : i));
      return [...prev, { ...item, quantity: 1 }];
    });
  };

  const updateQuantity = (id, delta) => {
    setCart(cart.map((item) => (item.id === id ? { ...item, quantity: Math.max(0, item.quantity + delta) } : item)).filter((item) => item.quantity > 0));
  };

  // Bundle helpers.
  // Stable signature of a bundle's content used to merge duplicates in cart.
  const bundleSignature = (items) => {
    const sig = items
      .filter((i) => !i.isDiscount)
      .map((i) => ({
        baseId: String(i.id).replace(/-\d{13,}$/, ''),
        name: i.name,
        slot: i._bundleSlot ?? null,
        price: i.price,
        toppings: (i._bundleToppings || []).map((t) => `${t.id}:${t.qty}`).sort().join(','),
      }))
      .sort((a, b) => `${a.slot}|${a.baseId}`.localeCompare(`${b.slot}|${b.baseId}`));
    return JSON.stringify(sig);
  };

  // groupCartByBundle: returns ordered render groups for the cart modal.
  // Identical bundles (same content signature) are merged into one group
  // with quantity > 1, so the customer sees `Meal Deal for 2 × 2` instead
  // of two separate cards.
  //   { kind: 'bundle', bundleIds: [...], bundleType, name, items, total, quantity }
  //   { kind: 'item', item }
  const groupCartByBundle = () => {
    const seen = new Set();
    const sigToGroup = new Map();
    const groups = [];
    cart.forEach((item) => {
      if (item._bundleId) {
        if (seen.has(item._bundleId)) return;
        seen.add(item._bundleId);
        const bundleItems = cart.filter((c) => c._bundleId === item._bundleId);
        const header = bundleItems.find((c) => c._bundleName) || bundleItems[0];
        const total = bundleItems.reduce((s, c) => s + c.price * c.quantity, 0);
        const sig = bundleSignature(bundleItems);
        const existing = sigToGroup.get(sig);
        if (existing) {
          existing.bundleIds.push(item._bundleId);
          existing.total += total;
          existing.quantity += 1;
          return;
        }
        const group = {
          kind: 'bundle',
          bundleIds: [item._bundleId],
          bundleType: item._bundleType,
          name: header._bundleName || (item._bundleType === 'bogo' ? 'Buy One Get One Free' : 'Meal Deal'),
          items: bundleItems,
          total,
          quantity: 1,
        };
        sigToGroup.set(sig, group);
        groups.push(group);
      } else {
        groups.push({ kind: 'item', item });
      }
    });
    return groups;
  };

  const removeBundle = (bundleId) => {
    setCart((prev) => prev.filter((item) => item._bundleId !== bundleId));
  };

  // Clone every line of an existing bundle under a fresh bundleId so the
  // customer can have N copies of the same meal deal / BOGO in their cart.
  // Each clone keeps the original prices, toppings, and discount line.
  const duplicateBundle = (bundleId) => {
    setCart((prev) => {
      const bundleItems = prev.filter((c) => c._bundleId === bundleId);
      if (bundleItems.length === 0) return prev;
      const stamp = Date.now();
      let serial = 1000;
      const bundleType = bundleItems[0]._bundleType;
      const dealId = bundleItems.find((i) => i._bundleDealId)?._bundleDealId;
      const newBundleId = bundleType === 'mealdeal' && dealId
        ? `mealdeal-${dealId}-${stamp}`
        : `${bundleType}-${stamp}`;
      const cloned = bundleItems.map((item) => {
        if (item.isDiscount) {
          // Match the original discount id shape so the server can route it.
          const newDiscountId = bundleType === 'bogo'
            ? `bogo-discount-${stamp}`
            : `discount-${dealId}-${stamp}`;
          return { ...item, id: newDiscountId, _bundleId: newBundleId, quantity: 1 };
        }
        const baseId = String(item.id).replace(/-\d{13,}$/, '');
        return { ...item, id: `${baseId}-${stamp}${serial++}`, _bundleId: newBundleId };
      });
      return [...prev, ...cloned];
    });
  };

  // Open the bundle's source page with editingBundle set. We do NOT remove the
  // items from the cart yet — that only happens when the user confirms the
  // edit. If they back out, the original bundle stays intact.
  const editBundle = (bundleId) => {
    const bundleItems = cart.filter((item) => item._bundleId === bundleId);
    if (bundleItems.length === 0) return;
    const bundleType = bundleItems[0]._bundleType;
    const header = bundleItems.find((c) => c._bundleDealId);
    setEditingBundle({
      bundleId,
      bundleType,
      dealId: header?._bundleDealId,
      items: bundleItems,
    });
    setShowCart(false);
    setActiveSection(bundleType === 'bogo' ? 'bogo' : 'mealdeals');
  };

  // Page-level back: leaving without confirming should drop the edit context
  // so the bundle isn't left mid-edit on the next visit.
  const exitToHome = () => {
    setEditingBundle(null);
    setActiveSection('home');
  };

  const getTotal = () => cart.reduce((sum, item) => sum + item.price * item.quantity, 0).toFixed(2);
  // Count loose items at their quantity, but treat each bundle (BOGO / meal deal)
  // as a single item regardless of how many lines back it.
  const getCartItemCount = () => {
    const seenBundles = new Set();
    let count = 0;
    for (const item of cart) {
      if (item.isDiscount) continue;
      if (item._bundleId) {
        if (seenBundles.has(item._bundleId)) continue;
        seenBundles.add(item._bundleId);
        count += 1;
      } else {
        count += item.quantity;
      }
    }
    return count;
  };

  const getFinalTotal = () => parseFloat(getTotal()).toFixed(2);

  const subtotal = parseFloat(getTotal());
  const meetsMinOrder = subtotal >= MIN_ORDER;
  const amountToMin = (MIN_ORDER - subtotal).toFixed(2);

  const handleStartCheckout = () => {
    if (!meetsMinOrder) return;
    if (!user) { setAuthMode('signin'); setShowAuth(true); return; }
    setShowCart(false);
    setShowCheckout(true);
  };

  const saveProfileFields = async (fields) => {
    if (!user) return;
    const sanitized = {
      name: fields.name || '',
      phone: fields.phone || '',
      address: fields.address || '',
      postcode: fields.postcode || '',
    };
    await setDoc(doc(db, 'users', user.uid), sanitized, { merge: true });
    setUserProfile((prev) => ({ ...(prev || {}), ...sanitized }));
  };

  const handlePlaceOrder = async () => {
    if (!validateForm()) return;
    // The user may have verified their email in another tab or since this
    // page loaded. reload() refreshes emailVerified; getIdToken(true) refreshes
    // the token claim the createPaymentIntent function checks server-side.
    if (user) {
      try {
        await reload(user);
        await user.getIdToken(true);
      } catch (e) {
        console.error('Could not refresh auth state:', e);
      }
    }
    if (!user || !user.emailVerified) {
      setPaymentError('Please verify your email before placing an order.');
      return;
    }
    setIsSubmitting(true); setPaymentError('');
    if (saveDetails) {
      saveProfileFields({
        name: formData.name,
        phone: formData.phone,
        address: formData.address,
        postcode: formData.postcode,
      }).catch((err) => console.error('Failed to save profile details:', err));
    }
    try {
      const fn = httpsCallable(functions, 'createPaymentIntent');
      const result = await fn({
        customer: { name: formData.name, email: formData.email, phone: formData.phone, address: formData.address, postcode: formData.postcode },
        // Send `price` so the server can validate the topping uplift on customized
        // pizzas, and `name` so it shows up correctly on the receipt.
        items: cart.map((item) => ({
          id: item.id,
          quantity: item.quantity,
          price: item.price,
          name: item.name,
        })),
        userId: user.uid,
      });
      const { clientSecret: secret, orderId, total } = result.data;
      setCurrentOrderId(orderId); setServerTotal(total);

      if (Capacitor.isNativePlatform()) {
        await presentNativePaymentSheet(secret, orderId, total);
      } else {
        setClientSecret(secret); setPaymentStep('payment');
      }
    } catch (error) {
      setPaymentError(error.message || 'Error processing order. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const presentNativePaymentSheet = async (secret, orderId, total) => {
    try {
      await NativeStripe.createPaymentSheet({
        paymentIntentClientSecret: secret,
        merchantDisplayName: 'PomoPizza',
        countryCode: 'GB',
        enableApplePay: true,
        applePayMerchantId: APPLE_PAY_MERCHANT_ID,
        enableGooglePay: true,
        GooglePayIsTesting: GOOGLE_PAY_TEST_MODE,
      });
      const { paymentResult } = await NativeStripe.presentPaymentSheet();
      if (paymentResult === 'paymentSheetCompleted') {
        setPaymentStep('success');
      } else if (paymentResult === 'paymentSheetCanceled') {
        // User dismissed the sheet — stay on details, no error.
      } else {
        setPaymentError('Payment did not complete. Please try again.');
      }
    } catch (err) {
      console.error('Native PaymentSheet error', err);
      setPaymentError(err?.message || 'Payment failed. Please try again.');
    }
  };

  const handlePaymentSuccess = () => setPaymentStep('success');
  const handlePaymentError = (msg) => setPaymentError(msg);

  const handleCloseCheckout = () => {
    setShowCheckout(false); setPaymentStep('details'); setClientSecret('');
    setCurrentOrderId(''); setServerTotal(0); setPaymentError('');
  };

  const handleOrderComplete = () => {
    setShowCheckout(false); setCart([]);
    setFormData({
      name: userProfile?.name || '',
      email: userProfile?.email || '',
      phone: userProfile?.phone || '',
      address: '', postcode: '',
    });
    setOutOfRange(false); setDistanceCheckFailed(false); setPaymentStep('details'); setClientSecret('');
    setCurrentOrderId(''); setServerTotal(0); setPaymentError('');
    setActiveSection('home');
  };

  const handleSignOut = async () => {
    try { await signOut(auth); } catch (err) { console.error('Sign out error:', err); }
  };

  const handleDeleteAccount = async () => {
    const deleteAccountFn = httpsCallable(functions, 'deleteAccount');
    await deleteAccountFn();
    try { await signOut(auth); } catch (err) { console.error('Sign out after delete failed:', err); }
    setShowProfile(false);
    setActiveSection('home');
  };

  const handleHomeNavigate = (section) => setActiveSection(section);

  function renderProfileModal() {
    return (
      <ProfileModal
        user={user}
        profile={userProfile}
        onClose={() => setShowProfile(false)}
        onSave={saveProfileFields}
        onSignOut={async () => { setShowProfile(false); await handleSignOut(); }}
        onDeleteAccount={handleDeleteAccount}
        onShowPrivacy={() => setShowPrivacy(true)}
      />
    );
  }

  function renderCartModal() {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b flex justify-between sticky top-0 bg-white">
            <h3 className="text-2xl font-bold">Cart</h3>
            <button onClick={() => setShowCart(false)}><X size={24} /></button>
          </div>
          <div className="p-6">
            {cart.length === 0 ? (
              <p className="text-center py-8 text-gray-500">Your cart is empty</p>
            ) : (
              <>
                {groupCartByBundle().map((group) => {
                  if (group.kind === 'bundle') {
                    const firstBundleId = group.bundleIds[0];
                    const lastBundleId = group.bundleIds[group.bundleIds.length - 1];
                    return (
                      <div key={firstBundleId} className="rounded mb-4 p-4 bg-amber-50 border border-amber-200">
                        <div className="flex justify-between items-start gap-3 mb-3">
                          <div className="min-w-0">
                            <h4 className="font-bold text-gray-800 truncate">{group.name}</h4>
                            <p className="text-xs text-gray-500 mt-0.5">Bundle{group.quantity > 1 ? ` × ${group.quantity}` : ''}</p>
                          </div>
                          <span className="font-bold flex-shrink-0" style={{ color: MARINARA }}>£{group.total.toFixed(2)}</span>
                        </div>
                        <ul className="space-y-1 text-sm text-gray-700 mb-3 pl-1">
                          {group.items.filter((c) => !c.isDiscount).map((c) => (
                            <li key={c.id} className="flex items-baseline gap-2">
                              <span className="text-gray-400">•</span>
                              <span className="flex-1">{c.name}</span>
                            </li>
                          ))}
                        </ul>
                        <div className="flex items-center gap-2">
                          <button onClick={() => editBundle(firstBundleId)}
                            className="flex-1 py-2 rounded-lg font-semibold text-sm transition-colors border-2"
                            style={{ borderColor: MARINARA, color: MARINARA, backgroundColor: 'white' }}
                            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f7e8e7'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'white'; }}>
                            Edit
                          </button>
                          <div className="flex items-center gap-2 bg-white rounded-lg border-2 px-1.5 py-1" style={{ borderColor: MARINARA }}>
                            <button onClick={() => removeBundle(lastBundleId)}
                              aria-label="Remove one"
                              className="w-7 h-7 rounded-full flex items-center justify-center text-white font-bold transition-colors"
                              style={{ backgroundColor: MARINARA }}
                              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = MARINARA_DARK}
                              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = MARINARA}>
                              <Minus size={14} />
                            </button>
                            <span className="w-6 text-center font-bold text-gray-800">{group.quantity}</span>
                            <button onClick={() => duplicateBundle(firstBundleId)}
                              aria-label="Add another"
                              className="w-7 h-7 rounded-full flex items-center justify-center text-white font-bold transition-colors"
                              style={{ backgroundColor: MARINARA }}
                              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = MARINARA_DARK}
                              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = MARINARA}>
                              <Plus size={14} />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  }
                  const item = group.item;
                  return (
                    <div key={item.id} className={`flex justify-between items-center p-4 rounded mb-4 ${item.isDiscount ? 'bg-green-50' : 'bg-gray-50'}`}>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold truncate">{item.name}</h4>
                        <p className={`text-sm ${item.isDiscount ? 'text-green-600' : 'text-gray-600'}`}>£{item.price.toFixed(2)}</p>
                      </div>
                      {!item.isDiscount && (
                        <div className="flex items-center gap-3 mx-3">
                          <button onClick={() => updateQuantity(item.id, -1)} className="bg-gray-200 p-1 rounded"><Minus size={16} /></button>
                          <span className="w-8 text-center">{item.quantity}</span>
                          <button onClick={() => updateQuantity(item.id, 1)} className="bg-gray-200 p-1 rounded"><Plus size={16} /></button>
                        </div>
                      )}
                      <span className={`font-semibold ${item.isDiscount ? 'text-green-600' : ''}`}>
                        {item.isDiscount ? `-£${Math.abs(item.price * item.quantity).toFixed(2)}` : `£${(item.price * item.quantity).toFixed(2)}`}
                      </span>
                    </div>
                  );
                })}

                <div className="border-t pt-4 mb-4">
                  <div className="flex justify-between text-xl font-bold"><span>Total:</span><span style={{ color: MARINARA }}>£{getTotal()}</span></div>
                </div>

                {!meetsMinOrder && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4 flex items-start gap-3">
                    <AlertTriangle className="text-amber-600 flex-shrink-0 mt-0.5" size={20} />
                    <div>
                      <p className="font-bold text-amber-900">Minimum order is £{MIN_ORDER.toFixed(2)}</p>
                      <p className="text-sm text-amber-800 mt-1">Add £{amountToMin} more to checkout.</p>
                    </div>
                  </div>
                )}

                {!user && meetsMinOrder && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4 text-sm text-blue-800">
                    You'll need to sign in or create an account to checkout.
                  </div>
                )}

                <button onClick={handleStartCheckout} disabled={!meetsMinOrder}
                  className="w-full text-white py-4 rounded-lg font-bold disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                  style={{ backgroundColor: meetsMinOrder ? MARINARA : undefined }}
                  onMouseEnter={(e) => meetsMinOrder && (e.currentTarget.style.backgroundColor = MARINARA_DARK)}
                  onMouseLeave={(e) => meetsMinOrder && (e.currentTarget.style.backgroundColor = MARINARA)}>
                  {!meetsMinOrder ? `Add £${amountToMin} more to checkout` : !user ? 'Sign In to Checkout' : 'Checkout'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  function renderCheckoutModal() {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto">
        <div className="bg-white rounded-lg max-w-2xl w-full my-8">
          <div className="p-6 border-b flex justify-between">
            <h3 className="text-2xl font-bold">
              {paymentStep === 'details' && 'Checkout'}
              {paymentStep === 'payment' && 'Payment'}
              {paymentStep === 'success' && 'Order Confirmed'}
            </h3>
            <button onClick={handleCloseCheckout}><X size={24} /></button>
          </div>

          {paymentStep === 'details' && (
            <div className="p-6 space-y-4">
              {user && user.emailVerified && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800 flex items-center gap-2">
                  <CheckCircle size={16} /> Signed in as {user.email}
                </div>
              )}
              {user && !user.emailVerified && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900 flex items-start gap-2">
                  <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold">Please verify your account</p>
                    <p className="text-amber-800">We sent a verification link to {user.email}. Open it to complete checkout.</p>
                  </div>
                </div>
              )}
              <input type="text" placeholder="Name *" value={formData.name} onChange={(e) => handleInputChange('name', e.target.value)} className="w-full px-4 py-3 border rounded-lg" />
              {formErrors.name && <p className="text-red-500 text-sm">{formErrors.name}</p>}
              <input type="email" placeholder="Email *" value={formData.email} onChange={(e) => handleInputChange('email', e.target.value)} className="w-full px-4 py-3 border rounded-lg" />
              {formErrors.email && <p className="text-red-500 text-sm">{formErrors.email}</p>}
              <input type="tel" placeholder="Phone *" value={formData.phone} onChange={(e) => handleInputChange('phone', e.target.value)} className="w-full px-4 py-3 border rounded-lg" />
              {formErrors.phone && <p className="text-red-500 text-sm">{formErrors.phone}</p>}
              <div className="relative">
                <input type="text" placeholder="Address *" value={formData.address} onChange={(e) => handleInputChange('address', e.target.value)}
                  onFocus={() => formData.address && getAutocompletePredictions(formData.address)}
                  onBlur={() => setTimeout(() => setShowAddressDropdown(false), 200)}
                  className="w-full px-4 py-3 border rounded-lg" autoComplete="off" />
                {showAddressDropdown && addressPredictions.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {addressPredictions.map((p, i) => (
                      <div key={i} onClick={() => handlePredictionSelect(p)} className="px-4 py-3 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0">
                        <p className="text-sm text-gray-800">{p.description}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {formErrors.address && <p className="text-red-500 text-sm">{formErrors.address}</p>}
              <input type="text" placeholder="Postcode *" value={formData.postcode}
                onChange={(e) => handleInputChange('postcode', e.target.value)}
                className="w-full px-4 py-3 border rounded-lg" autoComplete="off" />
              {formErrors.postcode && <p className="text-red-500 text-sm">{formErrors.postcode}</p>}
              {user && (
                <label className="flex items-center gap-2 text-sm text-gray-700 select-none cursor-pointer">
                  <input type="checkbox" checked={saveDetails} onChange={(e) => setSaveDetails(e.target.checked)} className="h-4 w-4" />
                  Save my details for next time
                </label>
              )}
              {checkingDistance && (
                <div className="text-sm text-gray-600">Checking delivery range…</div>
              )}
              {outOfRange && !checkingDistance && (
                <div className="flex items-center gap-2 text-red-700 text-sm bg-red-50 border border-red-200 p-3 rounded-lg">
                  <AlertCircle size={16} />
                  Sorry, we only deliver within {maxDeliveryMiles} miles of our restaurant. Please check your address.
                </div>
              )}
              {distanceCheckFailed && !checkingDistance && (
                <div className="flex items-center gap-2 text-amber-800 text-sm bg-amber-50 border border-amber-200 p-3 rounded-lg">
                  <AlertCircle size={16} />
                  We couldn't verify your delivery address. Please double-check it, or try again in a moment.
                </div>
              )}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex justify-between mb-2"><span>Subtotal:</span><span>£{getTotal()}</span></div>
                <div className="flex justify-between mb-2"><span>Delivery:</span><span>FREE</span></div>
                <div className="border-t pt-2 flex justify-between font-bold text-xl"><span>Total:</span><span style={{ color: MARINARA }}>£{getFinalTotal()}</span></div>
              </div>
              {paymentError && <div className="flex items-center gap-2 text-red-500 text-sm bg-red-50 p-3 rounded-lg"><AlertCircle size={16} />{paymentError}</div>}
              <button onClick={handlePlaceOrder} disabled={isSubmitting || checkingDistance || outOfRange || distanceCheckFailed || !user || !user.emailVerified}
                className="w-full text-white py-4 rounded-lg font-bold disabled:bg-gray-400 flex items-center justify-center gap-2"
                style={{ backgroundColor: (isSubmitting || checkingDistance || outOfRange || distanceCheckFailed || !user || !user.emailVerified) ? undefined : MARINARA }}>
                {isSubmitting ? <><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>Processing...</> : !user || !user.emailVerified ? 'Verify Email to Continue' : 'Continue to Payment'}
              </button>
            </div>
          )}

          {paymentStep === 'payment' && clientSecret && (
            <div className="p-6">
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="flex justify-between mb-2"><span>Delivering to:</span><span className="text-right text-sm">{formData.address}, {formData.postcode}</span></div>
                <div className="border-t pt-2 flex justify-between font-bold text-xl"><span>Total:</span><span style={{ color: MARINARA }}>£{serverTotal.toFixed(2)}</span></div>
              </div>
              {stripePromise ? (
                <>
                  <Elements stripe={stripePromise} options={{ clientSecret, appearance: stripeAppearance }}>
                    <PaymentForm orderId={currentOrderId} total={serverTotal} onSuccess={handlePaymentSuccess} onError={handlePaymentError} />
                  </Elements>
                  <button onClick={() => setPaymentStep('details')} className="mt-4 w-full text-gray-600 py-2 text-sm hover:text-gray-800">Back to details</button>
                </>
              ) : (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-yellow-800">Stripe not configured. Please set REACT_APP_STRIPE_PUBLISHABLE_KEY.</p>
                  <button onClick={() => setPaymentStep('details')} className="mt-4 w-full text-gray-600 py-2 text-sm hover:text-gray-800">Back to details</button>
                </div>
              )}
            </div>
          )}

          {paymentStep === 'success' && (
            <div className="p-6 text-center">
              <div className="mb-6">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4"><CheckCircle size={40} className="text-green-600" /></div>
                <h4 className="text-2xl font-bold text-gray-800 mb-2">Payment Successful</h4>
                <p className="text-gray-600">Thank you for your order</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 text-left mb-6">
                <div className="flex justify-between mb-2"><span className="text-gray-600">Order ID:</span><span className="font-mono text-sm">{currentOrderId}</span></div>
                <div className="flex justify-between mb-2"><span className="text-gray-600">Amount Paid:</span><span className="font-bold">£{serverTotal.toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-gray-600">Delivery to:</span><span className="text-right text-sm">{formData.postcode}</span></div>
              </div>
              <p className="text-sm text-gray-500 mb-6">A confirmation email has been sent to {formData.email}</p>
              <button onClick={handleOrderComplete} className="w-full text-white py-4 rounded-lg font-bold" style={{ backgroundColor: MARINARA }}>Done</button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Loading screen
  if (loading || !authReady) {
    return (
      <>
        <style>{GLOBAL_STYLES}</style>
        <div className="min-h-screen flex items-center justify-center relative"
          style={{ backgroundImage: `url(${BG_IMAGE})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
          <div className="absolute inset-0 bg-black bg-opacity-40" />
          <img src={LOGO_IMAGE} alt="" className="relative w-32 h-32 object-contain animate-spin drop-shadow-2xl"
            style={{ animationDuration: '2.5s' }} />
        </div>
      </>
    );
  }

  // Home
  if (activeSection === 'home') {
    return (
      <>
        <style>{GLOBAL_STYLES}</style>
        <HomeScreen onNavigate={handleHomeNavigate} user={user}
          onSignIn={() => { setAuthMode('signin'); setShowAuth(true); }}
          onShowProfile={() => setShowProfile(true)}
          onShowCart={() => setShowCart(true)}
          cartCount={getCartItemCount()} />
        {showCart && renderCartModal()}
        {showCheckout && renderCheckoutModal()}
        {showAuth && <AuthModal onClose={() => setShowAuth(false)} defaultMode={authMode} />}
        {showProfile && renderProfileModal()}
        {showPrivacy && <PrivacyPolicyModal onClose={() => setShowPrivacy(false)} />}
        {pizzaToCustomize && <PizzaCustomizer pizza={pizzaToCustomize} menuItems={menuItems} onClose={() => setPizzaToCustomize(null)} onAddToCart={addToCart} />}
      </>
    );
  }

  // Buy One Get One Free full page
  if (activeSection === 'bogo') {
    return (
      <>
        <style>{GLOBAL_STYLES}</style>
        <BOGOPage menuItems={menuItems} onBack={exitToHome} onAddToCart={addToCart}
          user={user}
          onSignIn={() => { setAuthMode('signin'); setShowAuth(true); }}
          onShowProfile={() => setShowProfile(true)}
          onShowCart={() => setShowCart(true)}
          cartCount={getCartItemCount()} cartTotal={getTotal()}
          editingBundle={editingBundle?.bundleType === 'bogo' ? editingBundle : null}
          onEditDone={() => {
            if (editingBundle) removeBundle(editingBundle.bundleId);
            setEditingBundle(null);
          }} />
        {showCart && renderCartModal()}
        {showCheckout && renderCheckoutModal()}
        {showAuth && <AuthModal onClose={() => setShowAuth(false)} defaultMode={authMode} />}
        {showProfile && renderProfileModal()}
        {showPrivacy && <PrivacyPolicyModal onClose={() => setShowPrivacy(false)} />}
        {pizzaToCustomize && <PizzaCustomizer pizza={pizzaToCustomize} menuItems={menuItems} onClose={() => setPizzaToCustomize(null)} onAddToCart={addToCart} />}
      </>
    );
  }

  // Meal Deals full page
  if (activeSection === 'mealdeals') {
    return (
      <>
        <style>{GLOBAL_STYLES}</style>
        <MealDealPage menuItems={menuItems} onBack={exitToHome} onAddToCart={addToCart}
          user={user}
          onSignIn={() => { setAuthMode('signin'); setShowAuth(true); }}
          onShowProfile={() => setShowProfile(true)}
          onShowCart={() => setShowCart(true)}
          cartCount={getCartItemCount()} cartTotal={getTotal()}
          editingBundle={editingBundle?.bundleType === 'mealdeal' ? editingBundle : null}
          onEditDone={() => {
            if (editingBundle) removeBundle(editingBundle.bundleId);
            setEditingBundle(null);
          }} />
        {showCart && renderCartModal()}
        {showCheckout && renderCheckoutModal()}
        {showAuth && <AuthModal onClose={() => setShowAuth(false)} defaultMode={authMode} />}
        {showProfile && renderProfileModal()}
        {showPrivacy && <PrivacyPolicyModal onClose={() => setShowPrivacy(false)} />}
        {pizzaToCustomize && <PizzaCustomizer pizza={pizzaToCustomize} menuItems={menuItems} onClose={() => setPizzaToCustomize(null)} onAddToCart={addToCart} />}
      </>
    );
  }

  // Menu / Location / Hours — pizza background, white cards on top
  return (
    <>
      <style>{GLOBAL_STYLES}</style>
      <div className="min-h-screen relative"
        style={{ backgroundImage: `url(${BG_IMAGE})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed' }}>
        <div className="absolute inset-0 bg-black bg-opacity-50 pointer-events-none" />

        <div className="relative z-10">
          {pizzaToCustomize && <PizzaCustomizer pizza={pizzaToCustomize} menuItems={menuItems} onClose={() => setPizzaToCustomize(null)} onAddToCart={addToCart} />}
          {showAuth && <AuthModal onClose={() => setShowAuth(false)} defaultMode={authMode} />}
          {showProfile && renderProfileModal()}
          {showPrivacy && <PrivacyPolicyModal onClose={() => setShowPrivacy(false)} />}

          <AppHeader
            user={user}
            onHome={() => setActiveSection('home')}
            onShowProfile={() => setShowProfile(true)}
            onSignIn={() => { setAuthMode('signin'); setShowAuth(true); }}
            onShowCart={() => setShowCart(true)}
            cartCount={getCartItemCount()}
          />

          {/* Nav */}
          <nav className="bg-white shadow-md sticky top-[68px] z-30">
            <div className="max-w-7xl mx-auto px-4">
              <div className="flex gap-6 overflow-x-auto">
                {['menu', 'location', 'hours'].map((section) => (
                  <button key={section} onClick={() => setActiveSection(section)}
                    className="py-4 px-2 font-semibold capitalize transition-colors border-b-2 text-sm whitespace-nowrap"
                    style={{
                      borderColor: activeSection === section ? MARINARA : 'transparent',
                      color: activeSection === section ? MARINARA : '#4b5563',
                    }}>
                    {section.charAt(0).toUpperCase() + section.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          </nav>

          <main className="max-w-7xl mx-auto px-4 py-12">
            {activeSection === 'menu' && (
              <div className="space-y-16">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <h2 className="text-4xl font-bold text-white drop-shadow-lg">Our Menu</h2>
                  <div className="flex gap-2">
                    <button onClick={() => setActiveSection('bogo')} className="text-white px-5 py-2 rounded-lg font-bold transition-colors flex items-center gap-2"
                      style={{ backgroundColor: MARINARA }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = MARINARA_DARK}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = MARINARA}>
                      <Tag size={16} /> Buy One Get One Free
                    </button>
                    <button onClick={() => setActiveSection('mealdeals')} className="bg-amber-400 text-amber-900 px-5 py-2 rounded-lg font-bold hover:bg-amber-500 transition-colors flex items-center gap-2">
                      <UtensilsCrossed size={16} /> Meal Deals
                    </button>
                  </div>
                </div>
                {/* Render categories in this fixed order: pizzas first, then everything else. */}
                {['pizza12', 'pizza14', 'starters', 'softDrinks', 'desserts', 'dips']
                  .map((key) => [key, menuItems[key] || []])
                  .map(([key, items]) => {
                  if (items.length === 0) return null;

                  // Soft drinks render as a compact list with +/− steppers per item.
                  if (key === 'softDrinks') {
                    return (
                      <div key={key}>
                        <h3 className="text-3xl font-bold mb-6 pb-2 text-white drop-shadow-lg border-b-2" style={{ borderColor: MARINARA }}>{categoryNames[key]}</h3>
                        <div className="bg-white rounded-lg shadow-md overflow-hidden divide-y divide-gray-100 max-w-3xl">
                          {items.map((item) => {
                            const inCart = cart.find((c) => c.id === item.id);
                            const qty = inCart?.quantity || 0;
                            return (
                              <div key={item.id} className="flex items-center justify-between gap-4 px-5 py-3">
                                <div className="min-w-0 flex-1">
                                  <p className="font-semibold text-gray-800 text-base">{item.name}</p>
                                  {item.description && <p className="text-xs text-gray-500 mt-0.5">{item.description}</p>}
                                </div>
                                <span className="font-bold text-base flex-shrink-0" style={{ color: MARINARA }}>£{item.price.toFixed(2)}</span>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                  <button
                                    type="button"
                                    onClick={() => updateQuantity(item.id, -1)}
                                    disabled={qty === 0}
                                    aria-label={`Remove one ${item.name}`}
                                    className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-base transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                                    style={{
                                      backgroundColor: qty === 0 ? '#e5e7eb' : MARINARA,
                                      color: qty === 0 ? '#9ca3af' : '#fff',
                                    }}>−</button>
                                  <span className="w-6 text-center font-bold text-gray-800">{qty}</span>
                                  <button
                                    type="button"
                                    onClick={() => addToCart(item)}
                                    aria-label={`Add one ${item.name}`}
                                    className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-base text-white transition-all"
                                    style={{ backgroundColor: MARINARA }}
                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = MARINARA_DARK}
                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = MARINARA}>+</button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div key={key}>
                      <h3 className="text-3xl font-bold mb-6 pb-2 text-white drop-shadow-lg border-b-2" style={{ borderColor: MARINARA }}>{categoryNames[key]}</h3>
                      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {items.map((item) => {
                          const isPizza = key === 'pizza12' || key === 'pizza14';
                          return (
                            <div key={item.id} className="bg-white rounded-lg shadow-md overflow-hidden flex flex-col">
                              {item.image && (
                                <div className="zoom-wrap w-full h-48">
                                  <img src={item.image} alt={item.name} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                                </div>
                              )}
                              <div className="p-6 flex flex-col flex-1">
                                <h4 className="text-2xl font-bold mb-2">{item.name}</h4>
                                {item.description && <p className="text-gray-600 text-base mb-4 italic flex-1">{item.description}</p>}
                                <div className="flex justify-between items-center gap-3">
                                  <span className="text-2xl font-bold flex-shrink-0" style={{ color: MARINARA }}>£{item.price.toFixed(2)}</span>
                                  {isPizza ? (
                                    <div className="flex gap-2">
                                      <button onClick={() => addToCart(item)} className="text-white px-4 py-2 rounded-lg font-semibold transition-colors"
                                        style={{ backgroundColor: MARINARA }}
                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = MARINARA_DARK}
                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = MARINARA}>
                                        Add
                                      </button>
                                      <button onClick={() => setPizzaToCustomize(item)} className="px-4 py-2 rounded-lg font-semibold transition-colors border-2"
                                        style={{ borderColor: MARINARA, color: MARINARA, backgroundColor: 'white' }}
                                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f7e8e7'; }}
                                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'white'; }}>
                                        Customize
                                      </button>
                                    </div>
                                  ) : (
                                    <button onClick={() => handleAddToCartClick(item)} className="text-white px-4 py-2 rounded-lg transition-colors"
                                      style={{ backgroundColor: MARINARA }}
                                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = MARINARA_DARK}
                                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = MARINARA}>
                                      Add
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {activeSection === 'location' && (
              <div className="max-w-3xl mx-auto bg-white rounded-lg shadow-lg p-8">
                <h2 className="text-3xl font-bold mb-8 text-center">Find Us</h2>
                <div className="space-y-6">
                  <div className="flex gap-4">
                    <MapPin className="flex-shrink-0" size={24} style={{ color: MARINARA }} />
                    <div>
                      <h3 className="font-bold text-xl mb-2">Address</h3>
                      <p>3 Charleville Road</p>
                      <p>London W14 9JL</p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <Phone className="flex-shrink-0" size={24} style={{ color: MARINARA }} />
                    <div>
                      <h3 className="font-bold text-xl mb-2">Contact</h3>
                      <p>Phone: <a href="tel:+442076102222" className="underline" style={{ color: MARINARA }}>020 7610 2222</a></p>
                      <p>Email: <a href="mailto:support@pomopizza.uk" className="underline" style={{ color: MARINARA }}>support@pomopizza.uk</a></p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeSection === 'hours' && (
              <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-lg p-8">
                <h2 className="text-3xl font-bold mb-8 text-center">Opening Hours</h2>
                <div className="flex gap-4">
                  <Clock className="flex-shrink-0" size={24} style={{ color: MARINARA }} />
                  <div className="flex-1 space-y-4">
                    <div className="flex justify-between border-b pb-3"><span className="font-semibold">Monday – Thursday</span><span>5:00 PM – 5:00 AM</span></div>
                    <div className="flex justify-between"><span className="font-semibold">Friday – Sunday</span><span>5:00 PM – 7:00 AM</span></div>
                  </div>
                </div>
              </div>
            )}
          </main>

          {showCart && renderCartModal()}
          {showCheckout && renderCheckoutModal()}
        </div>
      </div>
    </>
  );
};

// Top-level route switch — kept hookless so PomoPizzaApp's hooks are never
// called conditionally. pomopizza.uk/app shows the store-download landing
// page; everything else loads the main ordering app.
const App = () => {
  if (typeof window !== 'undefined' && window.location.pathname.replace(/\/$/, '') === '/app') {
    return <AppDownloadPage />;
  }
  return <PomoPizzaApp />;
};

export default App;